import React, { useEffect, useRef, useState } from 'react'
import Heading from '../../components/Heading'
import SubHeading from '../../components/SubHeading'
import Select from 'react-select';
import RichTextEditor from "../../components/Forms/RichTextEditor";
import MediaUpload from '../../components/Forms/MediaUpload';
import { useForm } from 'react-hook-form';
import { useCreateCampaign, useGetCampaignById, useUpdateCampaign } from '../../queries/campaign';
import { useLocation, useNavigate } from 'react-router-dom';
import { UploadBox } from '../User/components/UploadBox';
import { UploadSection } from '../User/components/UploadSection';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useGetChallengeCategoriesList, useGetChallengeList } from '../../queries/challenge';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useGetActiveOrganiser } from '../../queries/user';
import Dropdown from '../../components/Forms/Dropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NewDropdown from '../../components/Forms/NewDropdown';
import { useGetBlogs } from '../../queries/blog';
import { useGetProductsList } from '../../queries/products';
import { useGetEventList } from '../../queries/event';
import { useGetBrands } from '../../queries/brand';
import { useGetAds, useGetSlots } from '../../queries/advertisement';
import { useGetProjects } from '../../queries/project';
import { baseUrl } from '../../api/baseUrl';

const CreateNewCampaignCopy = () => {
    console.log('CreateNewCampaignCopy component loaded'); // Debug log

    const navigate = useNavigate();
    const { state } = useLocation();
    const campaignId = state?.campaignId;

    const [active, setActive] = useState(false);
    const [sponsor, setSponsor] = useState(null);
    const [mediaClear, setMediaClear] = useState(false);
    const [coverImage, setCoverImage] = useState(null);
    const [couponMedia, setCouponMedia] = useState(null);
    const [showInHomePage, setShowInHomePage] = useState(false);

    const [filteredChallengeList, setFilteredChallengeList] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const hasFormBeenPopulated = useRef(false);

    const { data: challengeList, isLoading: isChallengeListPending } = useGetChallengeList();
    const { data: challengeCategoryList, isLoading: isChallengeCategoryPending } = useGetChallengeCategoriesList();

    const { data: campaignDetails, isLoading } = useGetCampaignById(campaignId);
    const { mutateAsync: createCampaign, isPending: isCreating } = useCreateCampaign();
    const { mutateAsync: updateCampaign, isPending: isEditing } = useUpdateCampaign(campaignId);
    const { data: organiserList = [] } = useGetActiveOrganiser();
    const { data: blogList, isLoading: isBlogListLoading } = useGetBlogs('admin');
    const { data: eventList, isLoading: isEventListLoading } = useGetEventList();
    const { data: productsList, isLoading: isProductsLoading } = useGetProductsList();
    const { data: brandsList, isLoading: isBrandsLoading } = useGetBrands();
    const { data: adsList, isLoading: isAdsLoading } = useGetAds();
    const { data: slotsList, isLoading: isSlotsLoading } = useGetSlots();
    const { data: projectsList, isLoading: isProjectsLoading } = useGetProjects();

    const brandDropDown = brandsList && brandsList.map((brand) => ({ value: brand._id, label: brand.brandName }));

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        reset,
        trigger,
        watch,
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            title: "",
            description: "",
            imageUrl: "",
            startDate: "",
            endDate: "",
            campaignBudget: "",
            companyId: "",
            NGOId: "",
            sponsorType: "",
            status: "",
            campaignType: "",
            pricingModel: "",
            reportFrequency: "",
            reportRecipientEmail: "",
            sponsor: "",
            connectedChallenge: "",
            shortDescription: "",
            longDescription: "",
            productIdsArray: [],
            reportFrequency: "",
            rewardType: "",
            typeOfDiscount: "",
            discountValue: 0,
            couponCodeFormat: "",
            linkToUseCoupon: "",     // keep this as the UI field name
            promotionType: "",
            brand: "",
            productServices: [],
            redemptionType: "",
            apiType: "",
            apiEndpoint: "",
            apiHeaders: "",
            apiBody: "",
            webhookUrl: "",
            typeOfApi: "", // Add this new field to match schema,
            dailyRunTime: "",
            notes: "",
            couponRedemptionInstructions: "",
            advertisement: "",
            advertisementSlot: "",
            kpi: [],
            kpiNames: [],
            // Plant Trees campaign specific fields
            plantationProject: "",
            projectName: "",
            projectLocation: "",
            projectAddress: "",
            projectShortDescription: "",
            projectLongDescription: "",
            projectDate: "",
            projectPhotos: [],
            spendPointsPerTree: 0,
            maxCertificateCount: 0,
            sponsorMessage: "",
        }
    });

    // Watch campaign type to dynamically apply validation
    const watchedCampaignType = watch("campaignType");
    const watchedRewardType = watch("rewardType");
    const watchedSponsor = watch("sponsor");
    const watchedPricingModel = watch("pricingModel");
    const watchedCampaignRateType = watch("campaignRateType");
    const watchedPaymentTerms = watch("paymentTerms");
    const watchedInvoiceFrequency = watch("invoiceFrequency");
    const watchedChallengeCategory = watch("challengeCategory");
    const watchedConnectedChallenge = watch("connectedChallenge");
    const watchedTypeOfDiscount = watch("typeOfDiscount");
    const watchedBlogId = watch("blogId");
    const watchedPromotionType = watch("promotionType");
    const watchedEventId = watch("eventId");
    const watchedCertificateAvailable = watch("certificateAvailable");
    const watchedProject = watch("project");
    const watchedAdvertisement = watch("advertisement");
    const watchedAdvertisementSlot = watch("advertisementSlot");
    const watchedKpi = watch("kpi");
    const watchedKpiNames = watch("kpiNames");
    const watchedReportFrequency = watch("reportFrequency");
    const watchedProductIdsArray = watch("productIdsArray");
    const watchedBrand = watch("brand");
    const watchedProductServices = watch("productServices");
    const watchedCouponCodeFormat = watch("couponCodeFormat");
    const watchedRedemptionType = watch("redemptionType");
    const watchedApiType = watch("apiType");
    const watchedDailyRunTime = watch("dailyRunTime");
    const watchedNotes = watch("notes");
    const watchedCouponRedemptionInstructions = watch("couponRedemptionInstructions");
    // Plant Trees campaign watch variables
    const watchedPlantationProject = watch("plantationProject");
    const watchedSpendPointsPerTree = watch("spendPointsPerTree");
    const watchedMaxCertificateCount = watch("maxCertificateCount");

    // Debug form errors and state
    useEffect(() => {
        console.log('Form errors:', errors);
        console.log('Campaign type:', watchedCampaignType);
        console.log('Active state:', active);
    }, [errors, watchedCampaignType, active]);

    // Register validation rules for select fields
    useEffect(() => {
        register("sponsor", { required: "Client is required" });
        register("campaignType", { required: "Campaign Type is required" });
        register("rewardType");
        register("couponCodeFormat");
        register("brand");
        register("productServices");
        register("redemptionType");
        register("apiType");
        register("apiEndpoint");
        register("apiHeaders");
        register("apiBody");
        register("dailyRunTime");
        register("typeOfApi"); // Add this new field
        register("notes"); // Add notes field for all campaign types
        register("couponRedemptionInstructions"); // Add coupon redemption instructions field

        // Campaign type specific validation
        if (watchedCampaignType === 'Sponsor a Challenge') {
            register("connectedChallenge", { required: "Challenge is required" });
            register("startDate", { required: "Start Date is required" });
            register("endDate", { required: "End Date is required" });

            // Make rewards and redemption tracking fields required when active is true
            if (active) {
                // Rewards section - required fields
                register("rewardType", { required: "Reward Type is required" });
                // register("typeOfDiscount", { required: "Type of Discount is required" });
                // register("discountValue", { required: "Discount Value is required" });
                register("couponCodeFormat", { required: "Coupon Code Format is required" });
                // productServices is optional - brand-only campaigns don't need products
                register("productServices");

                // Either linkToUseCoupon or couponRedemptionInstructions is required
                const linkToUseCoupon = getValues("linkToUseCoupon");
                const couponRedemptionInstructions = getValues("couponRedemptionInstructions");

                if (!linkToUseCoupon && !couponRedemptionInstructions) {
                    register("linkToUseCoupon", {
                        required: "Either Link to Use Coupon or Coupon Redemption Instructions is required"
                    });
                    register("couponRedemptionInstructions", {
                        required: "Either Link to Use Coupon or Coupon Redemption Instructions is required"
                    });
                } else {
                    // If one field has value, make both optional
                    register("linkToUseCoupon", { required: false });
                    register("couponRedemptionInstructions", { required: false });
                }

                // Redemption tracking section - optional (can be "No Tracking")
                register("redemptionType");

                // API-specific required fields for Pull API
                if (watchedRedemptionType === 'PullApi') {
                    register("apiType", { required: "API Type is required" });
                    register("apiEndpoint", { required: "API Endpoint is required" });
                    register("apiHeaders", { required: "API Headers is required" });
                    register("dailyRunTime", { required: "Daily Run Time is required" });

                    if (watchedApiType === 'POST') {
                        register("apiBody", { required: "API Body is required" });
                    }
                }
            } else {
                // Reset to non-required when not active
                register("rewardType");
                register("typeOfDiscount");
                register("discountValue");
                register("couponCodeFormat");
                register("productServices");
                register("linkToUseCoupon");
                register("couponRedemptionInstructions");
                register("redemptionType");
                register("apiType");
                register("apiEndpoint");
                register("apiHeaders");
                register("apiBody");
                register("dailyRunTime");
            }
        }

        if (watchedCampaignType === 'Sponsor a Blog') {
            register("blogId", { required: "Blog is required" });
        }

        if (watchedCampaignType === 'Promote Brand/Product') {
            // register("productIdsArray", { required: "At least one product is required" });
            register("startDate", { required: "Start Date is required" });
            register("endDate", { required: "End Date is required" });

            // Make rewards and redemption tracking fields required when active is true (same as Sponsor a Challenge)
            if (active) {
                // Promotion configuration - required fields
                register("promotionType", { required: "Promotion Type is required" });

                // Conditional validation based on promotionType
                if (watchedPromotionType === 'brand') {
                    register("brand", { required: "Brand is required" });
                    register("productServices"); // Not required for brand promotion
                } else if (watchedPromotionType === 'product') {
                    register("brand"); // Not required but acts as filter for product promotion
                    register("productServices", { required: "Product/Services is required" });
                } else {
                    // Default state when promotionType is not selected
                    register("brand");
                    register("productServices");
                }

                // Rewards section - required fields
                register("rewardType", { required: "Reward Type is required" });
                // register("typeOfDiscount", { required: "Type of Discount is required" });
                // register("discountValue", { required: "Discount Value is required" });
                register("couponCodeFormat", { required: "Coupon Code Format is required" });

                // Either linkToUseCoupon or couponRedemptionInstructions is required
                const linkToUseCoupon = getValues("linkToUseCoupon");
                const couponRedemptionInstructions = getValues("couponRedemptionInstructions");

                if (!linkToUseCoupon && !couponRedemptionInstructions) {
                    register("linkToUseCoupon", {
                        required: "Either Link to Use Coupon or Coupon Redemption Instructions is required"
                    });
                    register("couponRedemptionInstructions", {
                        required: "Either Link to Use Coupon or Coupon Redemption Instructions is required"
                    });
                } else {
                    // If one field has value, make both optional
                    register("linkToUseCoupon", { required: false });
                    register("couponRedemptionInstructions", { required: false });
                }

                // Redemption tracking section - optional (can be "No Tracking")
                register("redemptionType");

                // API-specific required fields for Pull API
                if (watchedRedemptionType === 'PullApi') {
                    register("apiType", { required: "API Type is required" });
                    register("apiEndpoint", { required: "API Endpoint is required" });
                    register("apiHeaders", { required: "API Headers is required" });
                    register("dailyRunTime", { required: "Daily Run Time is required" });

                    if (watchedApiType === 'POST') {
                        register("apiBody", { required: "API Body is required" });
                    }
                }
            } else {
                // Reset to non-required when not active
                register("promotionType");
                register("brand");
                register("productServices");
                register("rewardType");
                register("typeOfDiscount");
                register("discountValue");
                register("couponCodeFormat");
                register("linkToUseCoupon");
                register("couponRedemptionInstructions");
                register("redemptionType");
                register("apiType");
                register("apiEndpoint");
                register("apiHeaders");
                register("apiBody");
                register("dailyRunTime");
            }
        }

        if (watchedCampaignType === 'Promote an Event') {
            register("eventId", { required: "Event is required" });
        }

        if (watchedCampaignType === 'Plant Trees') {
            if (active) {
                register("plantationProject", { required: "Plantation Project is required" });
                register("spendPointsPerTree", { required: "Spend Points per Tree is required" });
                register("maxCertificateCount", { required: "Max Certificate Count is required" });
                register("advertisement", { required: "Advertisement is required" });
                register("advertisementSlot", { required: "Advertisement Slot is required" });
            } else {
                register("plantationProject");
                register("spendPointsPerTree");
                register("maxCertificateCount");
                register("advertisement");
                register("advertisementSlot");
            }
        }

        if (watchedCampaignType === 'Advertisement') {
            if (active) {
                register("advertisement", { required: "Advertisement is required" });
                register("advertisementSlot", { required: "Advertisement Slot is required" });
                register("kpi", { required: "KPI is required" });
            } else {
                register("advertisement");
                register("advertisementSlot");
            }
        }
    }, [register, watchedCampaignType, active, watchedCouponCodeFormat, watchedRedemptionType, watchedApiType, watchedCouponRedemptionInstructions, watchedPromotionType, watchedAdvertisement, watchedAdvertisementSlot, getValues("linkToUseCoupon")]);

    // Auto-populate API fields when PullApi redemption type is selected
    useEffect(() => {
        if (watchedRedemptionType === 'PullApi') {
            const currentEndpoint = getValues("apiEndpoint");
            const currentHeaders = getValues("apiHeaders");
            // Only auto-populate if the fields are empty (don't overwrite user/backend data)
            if (!currentEndpoint) {
                setValue("apiEndpoint", `${baseUrl}/api/hooks/pull-api`);
            }
            if (!currentHeaders) {
                setValue("apiHeaders", JSON.stringify({ "client-secret": "" }, null, 2));
            }
            if (!getValues("apiType")) {
                setValue("apiType", "GET");
            }
        }
    }, [watchedRedemptionType, setValue, getValues]);

    // Auto-populate webhook fields when WebHook redemption type is selected
    useEffect(() => {
        if (watchedRedemptionType === 'WebHook') {
            const adminPanelUrl = window.location.origin;
            const currentWebhookUrl = getValues("webhookUrl");
            const currentLinkToUseCoupon = getValues("linkToUseCoupon");
            if (!currentWebhookUrl) {
                setValue("webhookUrl", `${adminPanelUrl}/webooktest`);
            }
            if (!currentLinkToUseCoupon) {
                setValue("linkToUseCoupon", `${adminPanelUrl}/webooktest`);
            }
        }
    }, [watchedRedemptionType, setValue, getValues]);

    // Add this useEffect to filter challenges when category changes
    useEffect(() => {
        if (selectedCategoryId && challengeList) {
            const filtered = challengeList.filter(challenge =>
                challenge.category?._id === selectedCategoryId || challenge.category === selectedCategoryId
            );
            setFilteredChallengeList(filtered);
        } else {
            setFilteredChallengeList(challengeList || []);
        }
    }, [selectedCategoryId, challengeList]);

    // Clear fields when promotionType changes for Promote Brand/Product campaigns
    // Skip during initial form population to avoid clearing backend data
    const prevPromotionType = useRef(watchedPromotionType);
    useEffect(() => {
        if (!hasFormBeenPopulated.current) return; // Don't clear during initial load
        // Only clear if the user actually changed the promotionType (not initial population)
        if (prevPromotionType.current === watchedPromotionType) return;
        prevPromotionType.current = watchedPromotionType;

        if (watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType) {
            if (watchedPromotionType === 'brand') {
                // Clear products when selecting brand promotion
                setValue("productServices", []);
                trigger("productServices"); // Trigger validation update
            } else if (watchedPromotionType === 'product') {
                // Clear brand when selecting product promotion
                setValue("brand", "");
                setValue("productServices", []);
                trigger(["brand", "productServices"]); // Trigger validation update
            }
        }
    }, [watchedPromotionType, watchedCampaignType, setValue, trigger]);

    // Ensure headers value is valid JSON string for PullApi campaigns
    const ensureValidJsonHeaders = (headersValue) => {
        if (!headersValue) return JSON.stringify({ "client-secret": "" });
        // If it's already valid JSON, return it as-is
        try {
            const parsed = JSON.parse(headersValue);
            return JSON.stringify(parsed);
        } catch (e) {
            // Not valid JSON - try to salvage by wrapping as client-secret default
            console.warn('Headers value is not valid JSON, using default:', headersValue);
            return JSON.stringify({ "client-secret": "" });
        }
    };

    // Handle challenge category change
    const handleCategoryChange = (e) => {
        const categoryId = e.target.value;
        setSelectedCategoryId(categoryId);
        setValue("challengeCategory", categoryId);
        setValue("connectedChallenge", ""); // reset challenge on category change
    };

    // Helper function to get filtered products based on selected brand or client
    const getFilteredProducts = () => {
        if (!productsList) return [];

        // Get currently selected product IDs to always include them in the list
        const selectedIds = Array.isArray(watchedProductServices) ? watchedProductServices : [];

        let filtered = productsList;

        // Priority 1: Filter by Brand if selected
        if (watchedBrand && watchedBrand !== "") {
            filtered = productsList.filter(product =>
                product.brandId === watchedBrand ||
                product.brandId?._id === watchedBrand ||
                product.brand === watchedBrand ||
                selectedIds.includes(product._id) // Always include already-selected products
            );
        }
        // Priority 2: Filter by Client (sponsor) if selected and no brand
        else if (watchedSponsor && watchedSponsor !== "") {
            filtered = productsList.filter(product => {
                const productCompanyId = typeof product.companyId === 'object' ? product.companyId?._id : product.companyId;
                const productNgoId = typeof product.ngoId === 'object' ? product.ngoId?._id : product.ngoId;
                return productCompanyId === watchedSponsor || productNgoId === watchedSponsor || selectedIds.includes(product._id); // Always include already-selected products
            });
        }

        return filtered;
    };

    // Helper function to generate time slots for daily run time
    // TEMPORARY: Now generates time slots every 5 minutes for custom time selection
    const getTimeSlots = () => {
        const timeSlots = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 5) {
                const hourStr = hour.toString().padStart(2, '0');
                const minuteStr = minute.toString().padStart(2, '0');
                const time24 = `${hourStr}:${minuteStr}`;

                // Convert to 12-hour format for display
                const isPM = hour >= 12;
                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const period = isPM ? 'PM' : 'AM';
                const time12 = `${hour12}:${minuteStr} ${period}`;

                timeSlots.push({
                    value: time24,
                    label: time12
                });
            }
        }
        return timeSlots;
    };

    // Helper function to get free advertisement slots (status = "Free")
    // For existing campaigns, also include the currently allocated slot
    const getFreeSlots = () => {
        if (!slotsList || !Array.isArray(slotsList)) {
            return [];
        }

        // Get free slots
        const freeSlots = slotsList.filter(slot => slot.status === "Free");

        // For existing campaigns, also include the currently allocated slot
        if (campaignId && campaignDetails && campaignDetails.advertisementSlot) {
            const currentlyAllocatedSlot = slotsList.find(slot =>
                slot._id === campaignDetails.advertisementSlot ||
                slot._id === campaignDetails.advertisementSlot._id
            );

            // If the currently allocated slot exists and is not already in free slots, add it
            if (currentlyAllocatedSlot && !freeSlots.find(slot => slot._id === currentlyAllocatedSlot._id)) {
                return [...freeSlots, currentlyAllocatedSlot];
            }
        }

        return freeSlots;
    };

    // Helper function to get filtered plantation projects (type = "Plantation")
    const getPlantationProjects = () => {
        if (!projectsList || !Array.isArray(projectsList)) {
            return [];
        }
        return projectsList.filter(project => project.type === "Plantation");
    };

    // Helper function to populate project fields when a project is selected
    const handlePlantationProjectChange = (projectId) => {
        const selectedProject = projectsList?.find(p => p._id === projectId);
        if (selectedProject) {
            setValue("projectName", selectedProject.name);
            setValue("projectLocation", selectedProject.location);
            setValue("projectAddress", selectedProject.address);
            setValue("projectShortDescription", selectedProject.shortDescription);
            setValue("projectLongDescription", selectedProject.longDescription);
            setValue("projectDate", selectedProject.eventDateTime);
            setValue("projectPhotos", selectedProject.projectImages || []);
        }
    };

    // Debug logging for data structure
    // useEffect(() => {
    //     console.log('brandsList:', brandsList);
    //     console.log('productsList:', productsList);
    //     if (brandsList && brandsList.length > 0) {
    //         console.log('first brand:', brandsList[0]);
    //     }
    //     if (productsList && productsList.length > 0) {
    //         console.log('first product:', productsList[0]);
    //     }
    // }, [brandsList, productsList]);

    // const onSubmit = async (data) => {
    //     try {
    //         // Validate required fields
    //         if (!data.sponsor) {
    //             setValue("sponsor", "");
    //             trigger("sponsor");
    //             toast.error("Client is required");
    //             return;
    //         }

    //         if (!data.campaignType) {
    //             setValue("campaignType", "");
    //             trigger("campaignType");
    //             toast.error("Campaign Type is required");
    //             return;
    //         }

    //         if (!data.connectedChallenge) {
    //             setValue("connectedChallenge", "");
    //             trigger("connectedChallenge");
    //             toast.error("Challenge is required");
    //             return;
    //         }

    //         const selected = organiserList.find(o => o._id === data.sponsor);

    //         const formData = {
    //             id: campaignId || null,
    //             active: active,
    //             title: data.title,
    //             description: data.description,
    //             file: coverImage,
    //             startDate: data.startDate,
    //             endDate: data.endDate,
    //             kpi: data.kpi,
    //             totalCarbonSavings: Number(data.totalCarbonSavings),
    //             totalRevenueGenerated: Number(data.totalRevenueGenerated),
    //             conversionRate: Number(data.conversionRate),
    //             campaignBudget: data.campaignBudget,
    //             pricingModel: data.pricingModel,
    //             campaignRateType: data.campaignRateType,
    //             rateValue: Number(data.rateValue),
    //             invoiceFrequency: data.invoiceFrequency,
    //             paymentTerms: data.paymentTerms,
    //             campaignType: data.campaignType,
    //             reportFrequency: data.reportFrequency,
    //             reportRecipientEmail: data.reportRecipientEmail,
    //             companyId: selected?.organiserType === 'Company' ? data.sponsor : null,
    //             NGOId: selected?.organiserType === 'NGO' ? data.sponsor : null,
    //             sponsorType: selected?.organiserType === 'NGO' ? 'ngo' : 'company',
    //             status: '',
    //             challengeId: data.connectedChallenge ? data.connectedChallenge : null,
    //             sponsorMessage: data.sponsorMessage || '',
    //             typeOfDiscount: data.typeOfDiscount || '',
    //             maxCouponCount: Number(data.maxCouponCount) || '',
    //             maxRedemptionCount: Number(data.maxRedemptionCount) || '',
    //             couponCodeFormat: data.couponCodeFormat || '',
    //             linkCoupon: data.linkCoupon || '',
    //             couponMedia: couponMedia || '',
    //             discountValue: Number(data.discountValue) || 0,
    //         };

    //         // console.log("Form Data:", formData);

    //         if (campaignId) {
    //             // Update existing campaign
    //             await updateCampaign(
    //                 formData,
    //                 {
    //                     onSuccess: (res) => {
    //                         console.log(res);
    //                         if (res.error) {
    //                             toast.error("Error updating campaign: " + res.error);
    //                         } else {
    //                             toast.success("Campaign updated successfully!");
    //                             setTimeout(() => {
    //                                 navigate("/campaign-management/list-campaign");
    //                             }, 2000);
    //                         }
    //                     }
    //                 });
    //         } else {
    //             // Create new campaign
    //             await createCampaign(
    //                 formData,
    //                 {
    //                     onSuccess: (res) => {
    //                         if (res.error) {
    //                             toast.error("Error creating campaign: " + res.error);
    //                         } else {
    //                             toast.success("Campaign created successfully!");
    //                             setTimeout(() => {
    //                                 navigate("/campaign-management/list-campaign");
    //                             }, 2000);
    //                         }
    //                     }
    //                 }
    //             );
    //         }
    //     } catch (error) {
    //         console.error('Error submitting campaign:', error);
    //         // Handle error (show toast, etc.)
    //     }
    // };

    const onSubmit = async (data) => {
        console.log('onSubmit called with data:', data); // Debug log
        console.log('active state:', active); // Debug log
        console.log('campaignType:', data.campaignType); // Debug log

        try {
            // Common validation
            if (!data.sponsor) {
                setValue("sponsor", "");
                trigger("sponsor");
                toast.error("Client is required");
                return;
            }

            if (!data.campaignType) {
                setValue("campaignType", "");
                trigger("campaignType");
                toast.error("Campaign Type is required");
                return;
            }

            // Campaign type specific validation
            if (data.campaignType === 'Sponsor a Challenge') {
                if (!data.connectedChallenge) {
                    setValue("connectedChallenge", "");
                    trigger("connectedChallenge");
                    toast.error("Challenge is required");
                    return;
                }
                if (!data.startDate) {
                    setValue("startDate", "");
                    trigger("startDate");
                    toast.error("Start Date is required");
                    return;
                }
                if (!data.endDate) {
                    setValue("endDate", "");
                    trigger("endDate");
                    toast.error("End Date is required");
                    return;
                }

                // Check if campaign is active and validate either/or requirement
                if (active) {
                    if (!data.linkToUseCoupon && !data.couponRedemptionInstructions) {
                        toast.error("Either Link to Use Coupon or Coupon Redemption Instructions is required");
                        return;
                    }
                }
            }

            if (data.campaignType === 'Promote Brand/Product') {
                // if (!data.productIdsArray || data.productIdsArray.length === 0) {
                //     setValue("productIdsArray", []);
                //     trigger("productIdsArray");
                //     toast.error("At least one product is required");
                //     return;
                // }
                if (!data.startDate) {
                    setValue("startDate", "");
                    trigger("startDate");
                    toast.error("Start Date is required");
                    return;
                }
                if (!data.endDate) {
                    setValue("endDate", "");
                    trigger("endDate");
                    toast.error("End Date is required");
                    return;
                }

                // Validate promotion type specific requirements (required for all campaigns, not just active)
                if (data.promotionType === 'product' && (!data.productServices || data.productServices.length === 0)) {
                    setValue("productServices", []);
                    trigger("productServices");
                    toast.error("Product/Services is required for product promotion campaigns");
                    return;
                }
                if (data.promotionType === 'brand' && (!data.brand || data.brand.length === 0)) {
                    setValue("brand", "");
                    trigger("brand");
                    toast.error("Brand is required for brand promotion campaigns");
                    return;
                }

                // Check if campaign is active and validate required fields
                if (active) {
                    // if (!data.rewardType) {
                    //     setValue("rewardType", "");
                    //     trigger("rewardType");
                    //     toast.error("Reward Type is required for active campaigns");
                    //     return;
                    // }
                    // if (!data.typeOfDiscount) {
                    //     setValue("typeOfDiscount", "");
                    //     trigger("typeOfDiscount");
                    //     toast.error("Type of Discount is required for active campaigns");
                    //     return;
                    // }
                    // if (!data.discountValue || data.discountValue === 0) {
                    //     setValue("discountValue", "");
                    //     trigger("discountValue");
                    //     toast.error("Discount Value is required for active campaigns");
                    //     return;
                    // }
                    // if (!data.couponCodeFormat) {
                    //     setValue("couponCodeFormat", "");
                    //     trigger("couponCodeFormat");
                    //     toast.error("Coupon Code Format is required for active campaigns");
                    //     return;
                    // }
                    // Product/Brand validation moved outside active block (now validated for all campaigns)
                    // Redemption Type is now optional - can be "No Tracking"

                    // Either linkToUseCoupon or couponRedemptionInstructions is required
                    if (!data.linkToUseCoupon && !data.couponRedemptionInstructions) {
                        toast.error("Either Link to Use Coupon or Coupon Redemption Instructions is required for active campaigns");
                        return;
                    }

                    // API-specific validation for Pull API
                    if (data.redemptionType === 'PullApi') {
                        if (!data.apiType) {
                            setValue("apiType", "");
                            trigger("apiType");
                            toast.error("API Type is required for Pull API redemption");
                            return;
                        }
                        if (!data.apiEndpoint) {
                            setValue("apiEndpoint", "");
                            trigger("apiEndpoint");
                            toast.error("API Endpoint is required for Pull API redemption");
                            return;
                        }
                        if (!data.apiHeaders) {
                            setValue("apiHeaders", "");
                            trigger("apiHeaders");
                            toast.error("API Headers are required for Pull API redemption");
                            return;
                        }
                        if (!data.dailyRunTime || data.dailyRunTime.length === 0) {
                            setValue("dailyRunTime", []);
                            trigger("dailyRunTime");
                            toast.error("Daily Run Time is required for Pull API redemption");
                            return;
                        }
                        if (data.apiType === 'POST' && !data.apiBody) {
                            setValue("apiBody", "");
                            trigger("apiBody");
                            toast.error("API Body is required for POST API calls");
                            return;
                        }
                    }
                }
            }

            if (data.campaignType === 'Sponsor a Blog' && !data.blogId) {
                setValue("blogId", "");
                trigger("blogId");
                toast.error("Blog is required");
                return;
            }

            // if (data.campaignType === 'Promote Brand/Product' && !data.blogId) {
            //     setValue("blogId", "");
            //     trigger("blogId");
            //     toast.error("Product is required");
            //     return;
            // }

            if (data.campaignType === 'Promote an Event' && !data.eventId) {
                setValue("eventId", "");
                trigger("eventId");
                toast.error("Event is required");
                return;
            }

            if (data.campaignType === 'Advertisement') {
                if (active && !data.advertisement) {
                    setValue("advertisement", "");
                    trigger("advertisement");
                    toast.error("Advertisement is required");
                    return;
                }
                if (active && !data.advertisementSlot) {
                    setValue("advertisementSlot", "");
                    trigger("advertisementSlot");
                    toast.error("Advertisement Slot is required");
                    return;
                }
            }

            // Plant Trees campaign validation
            if (data.campaignType === 'Plant Trees' && active) {
                if (!data.plantationProject) {
                    setValue("plantationProject", "");
                    trigger("plantationProject");
                    toast.error("Plantation Project is required");
                    return;
                }
                if (!data.spendPointsPerTree || data.spendPointsPerTree <= 0) {
                    setValue("spendPointsPerTree", "");
                    trigger("spendPointsPerTree");
                    toast.error("Spend Points per Tree is required and must be greater than 0");
                    return;
                }
                if (!data.maxCertificateCount || data.maxCertificateCount <= 0) {
                    setValue("maxCertificateCount", "");
                    trigger("maxCertificateCount");
                    toast.error("Max Certificate Count is required and must be greater than 0");
                    return;
                }
                if (!data.advertisement) {
                    setValue("advertisement", "");
                    trigger("advertisement");
                    toast.error("Advertisement is required");
                    return;
                }
                if (!data.advertisementSlot) {
                    setValue("advertisementSlot", "");
                    trigger("advertisementSlot");
                    toast.error("Advertisement Slot is required");
                    return;
                }
            }

            const selected = organiserList.find(o => o._id === data.sponsor);

            // Base form data - common to all campaign types
            const baseFormData = {
                id: campaignId || null,
                active: active,
                title: data.title || '',
                startDate: data.startDate ? new Date(data.startDate).toISOString() : '',
                endDate: data.endDate ? new Date(data.endDate).toISOString() : '',
                description: data.description || '',
                imageUrl: coverImage, // Changed from 'file' to match schema
                campaignBudget: Number(data.campaignBudget) || 0,
                pricingModel: data.pricingModel || '',
                campaignRateType: data.campaignRateType || '',
                rateValue: Number(data.rateValue) || 0,
                invoiceFrequency: data.invoiceFrequency || '',
                paymentTerms: data.paymentTerms || '',
                campaignType: data.campaignType,
                reportFrequency: data.reportFrequency || '',
                reportRecipientEmail: data.reportRecipientEmail || '',
                companyId: selected?.organiserType === 'Company' ? data.sponsor : null,
                NGOId: selected?.organiserType === 'NGO' ? data.sponsor : null,
                sponsorType: selected?.organiserType === 'NGO' ? 'ngo' : 'company',
                status: data.status || '',
                linkToUseCoupon: data.linkToUseCoupon || "",
                kpi: Array.isArray(data.kpiNames) && data.kpiNames.length > 0 ? data.kpiNames : (Array.isArray(data.kpi) ? data.kpi : []),
                notes: data.notes || '', // Add notes field common to all campaign types
            };

            // Add campaign type specific fields
            let formData = { ...baseFormData };

            switch (data.campaignType) {
                case 'Sponsor a Challenge':
                    // Ensure type safety for product IDs array (optional for challenges)
                    const challengeProductIds = Array.isArray(data.productServices) ? data.productServices : [];

                    formData = {
                        ...formData,
                        totalCarbonSavings: Number(data.totalCarbonSavings) || 0,
                        totalRevenueGenerated: Number(data.totalRevenueGenerated) || 0,
                        conversionRate: Number(data.conversionRate) || 0,
                        challengeId: data.connectedChallenge,
                        connectedChallenge: data.connectedChallenge, // Add this field as per schema
                        sponsorMessage: data.sponsorMessage || '',
                        typeOfDiscount: data.typeOfDiscount || '',
                        couponCodeFormat: data.couponCodeFormat || '',
                        discountValue: Number(data.discountValue) || 0,
                        couponMedia: couponMedia || '',
                        couponRedemptionInstructions: data.couponRedemptionInstructions || '',
                        brand: data.brand || '',
                        productServices: challengeProductIds, // ✅ Keep for backward compatibility
                        redemptionType: data.redemptionType || '',
                        typeOfApi: data.apiType || '', // Map apiType to typeOfApi for schema
                        apiEndpoint: data.apiEndpoint || '',
                        headers: data.redemptionType === 'PullApi' ? ensureValidJsonHeaders(data.apiHeaders) : (data.apiHeaders || ''),
                        apiBody: data.apiBody || '',
                        webhookUrl: data.webhookUrl || '',
                        dailyRunTime: data.dailyRunTime || [],
                        rewardType: data.rewardType || '',
                        productIdsArray: challengeProductIds, // ✅ Backend expects this field (type-safe mapping)
                    };
                    break;

                case 'Sponsor a Blog':
                    formData = {
                        ...formData,
                        blogId: data.blogId,
                        sponsorMessage: data.sponsorMessage || '',
                        link: data.link || '',
                        totalPageViews: Number(data.totalPageViews) || 0,
                        ctr: data.ctr || '',
                        comments: data.comments || '',
                        likes: data.likes || '',
                        sponsoredImage: couponMedia || '',
                    };
                    break;

                case 'Promote Brand/Product':
                    // Ensure type safety for product IDs array
                    const productIds = Array.isArray(data.productServices) ? data.productServices : [];

                    formData = {
                        ...formData,
                        startDate: data.startDate ? new Date(data.startDate).toISOString() : '',
                        endDate: data.endDate ? new Date(data.endDate).toISOString() : '',
                        totalCarbonSavings: Number(data.totalCarbonSavings) || 0,
                        totalRevenueGenerated: Number(data.totalRevenueGenerated) || 0,
                        conversionRate: Number(data.conversionRate) || 0,
                        promotionType: data.promotionType || '',
                        productIdsArray: productIds, // ✅ Backend expects this field (type-safe mapping)
                        promotionalLink: data.promotionalLink || '',
                        sponsorMessage: data.sponsorMessage || '',
                        typeOfDiscount: data.typeOfDiscount || '',
                        couponCodeFormat: data.couponCodeFormat || '',
                        discountValue: Number(data.discountValue) || 0,
                        couponMedia: couponMedia || '',
                        couponRedemptionInstructions: data.couponRedemptionInstructions || '',
                        brand: data.brand || '',
                        productServices: productIds, // ✅ Keep for backward compatibility
                        redemptionType: data.redemptionType || '',
                        typeOfApi: data.apiType || '', // Map apiType to typeOfApi for schema
                        apiEndpoint: data.apiEndpoint || '',
                        headers: data.redemptionType === 'PullApi' ? ensureValidJsonHeaders(data.apiHeaders) : (data.apiHeaders || ''),
                        apiBody: data.apiBody || '',
                        webhookUrl: data.webhookUrl || '',
                        dailyRunTime: data.dailyRunTime || [],
                        rewardType: data.rewardType || '',
                    };
                    break;

                case 'Promote an Event':
                    formData = {
                        ...formData,
                        totalNumberOfVolunteers: Number(data.totalNumberOfVolunteers) || 0,
                        totalHoursOfVolunteering: Number(data.totalHoursOfVolunteering) || 0,
                        eventId: data.eventId,
                        certificateAvailable: data.certificateAvailable || '',
                        certificateTemplate: data.certificateTemplate || '',
                        sponsorMessage: data.sponsorMessage || '',
                        certificateTemplateMedia: couponMedia || '', // You might want separate state for this
                        actualCertificateMedia: '', // You might want separate state for this
                        advertisement: data.advertisement || null,
                        adSlot: data.adSlot || null,
                        CCAdKPI: data.CCAdKPI || '',
                        adReportingFrequency: data.adReportingFrequency || '',
                    };
                    break;

                case 'Advertisement':
                    formData = {
                        ...formData,
                        reportFrequency: data.reportFrequency || '',
                        advertisement: data.advertisement || null,
                        advertisementSlot: data.advertisementSlot || null,
                    };
                    break;

                case 'Plant Trees':
                    const selectedProject = projectsList?.find(p => p._id === data.plantationProject);
                    formData = {
                        ...formData,
                        plantationProject: selectedProject || null,
                        projectDate: data.projectDate || selectedProject?.eventDateTime || '',
                        spendPointsPerTree: Number(data.spendPointsPerTree) || 0,
                        maxCertificateCount: Number(data.maxCertificateCount) || 0,
                        eventId: data.eventId || null,
                        advertisement: data.advertisement || null,
                        advertisementSlot: data.advertisementSlot || null,
                    };
                    break;

                default:
                    break;
            }

            console.log('Campaign Form Data:', formData); // Debug logging
            console.log('KPI DEBUG - data.kpi:', data.kpi, 'data.kpiNames:', data.kpiNames, 'formData.kpi:', formData.kpi);
            console.log('Form validation passed, submitting...'); // Additional debug
            toast.loading('Saving campaign data...');

            // Remove empty/null values to prevent overwriting existing data
            const cleanFormData = Object.entries(formData).reduce((acc, [key, value]) => {
                // Fields that should always be preserved even if empty (clearing them has meaning)
                const alwaysPreserveFields = ['active', 'id', 'imageUrl', 'campaignType', 'kpi', 'pricingModel', 'sponsorMessage', 'productServices', 'webhookUrl', 'redemptionType', 'typeOfDiscount', 'discountValue', 'couponCodeFormat', 'brand', 'promotionType', 'productIdsArray', 'startDate', 'endDate'];

                if (alwaysPreserveFields.includes(key)) {
                    // Always include these fields, even if empty
                    acc[key] = value;
                } else if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
                    // For other fields, only include if they have meaningful values
                    acc[key] = value;
                }
                return acc;
            }, {});

            console.log('Cleaned Form Data:', cleanFormData); // Debug log to see what's being sent

            // Submit the form data
            if (campaignId) {
                await updateCampaign(cleanFormData, {
                    onSuccess: (res) => {
                        toast.dismiss();
                        if (res.error) {
                            toast.error("Error updating campaign: " + res.error);
                        } else {
                            toast.success("Campaign updated successfully!");
                            setTimeout(() => {
                                navigate("/campaign-management/list-campaign");
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        toast.dismiss();
                        console.error('Error updating campaign:', error);
                        toast.error("Failed to update campaign. Please try again.");
                    }
                });
            } else {
                await createCampaign(cleanFormData, {
                    onSuccess: (res) => {
                        toast.dismiss();
                        if (res.error) {
                            toast.error("Error creating campaign: " + res.error);
                        } else {
                            toast.success("Campaign created successfully!");
                            setTimeout(() => {
                                navigate("/campaign-management/list-campaign");
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        toast.dismiss();
                        console.error('Error creating campaign:', error);
                        toast.error("Failed to create campaign. Please try again.");
                    }
                });
            }
        } catch (error) {
            toast.dismiss();
            console.error('Error submitting campaign:', error);
            toast.error("An error occurred while submitting the campaign");
        }
    };

    useEffect(() => {
        if (campaignDetails && !hasFormBeenPopulated.current) {
            hasFormBeenPopulated.current = true;
            // Set prevPromotionType before reset to prevent cleanup effect from clearing productServices
            prevPromotionType.current = campaignDetails.promotionType || "";
            console.log('KPI LOAD DEBUG - campaignDetails.kpi:', campaignDetails.kpi, 'type:', typeof campaignDetails.kpi, 'isArray:', Array.isArray(campaignDetails.kpi));

            // Get sponsor ID - it could be in companyId or NGOId field directly
            const sponsorId = campaignDetails.companyId?._id || campaignDetails.NGOId?._id;

            // Format dates for input fields (convert from ISO to YYYY-MM-DDTHH:MM in local time for datetime-local)
            const formatDateForInput = (isoDate) => {
                if (!isoDate) return "";
                const d = new Date(isoDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
            };

            reset({
                title: campaignDetails.title || "",
                description: campaignDetails.description || "",
                imageUrl: campaignDetails.imageUrl || "",
                startDate: formatDateForInput(campaignDetails.startDate),
                endDate: formatDateForInput(campaignDetails.endDate),
                campaignBudget: campaignDetails.campaignBudget || "",
                companyId: campaignDetails.companyId || "",
                NGOId: campaignDetails.NGOId || "",
                sponsorType: campaignDetails.sponsorType || "",
                status: campaignDetails.status || "",
                campaignType: campaignDetails.campaignType || "",
                pricingModel: campaignDetails.pricingModel || "",
                reportFrequency: campaignDetails.reportFrequency || "",
                reportRecipientEmail: campaignDetails.reportRecipientEmail || "",
                totalCarbonSavings: campaignDetails.totalCarbonSavings || "",
                totalRevenueGenerated: campaignDetails.totalRevenueGenerated || "",
                conversionRate: campaignDetails.conversionRate || "",
                campaignRateType: campaignDetails.campaignRateType || "",
                rateValue: campaignDetails.rateValue || 0,
                invoiceFrequency: campaignDetails.invoiceFrequency || "",
                paymentTerms: campaignDetails.paymentTerms || "",
                challengeCategory: (typeof campaignDetails.challengeId === 'object' && campaignDetails.challengeId?.category?._id) || (typeof campaignDetails.challengeId === 'object' && campaignDetails.challengeId?.category) || "",
                connectedChallenge: (typeof campaignDetails.challengeId === 'object' && campaignDetails.challengeId?._id) || campaignDetails.challengeId || "",
                typeOfDiscount: campaignDetails.typeOfDiscount || "",
                couponCodeFormat: campaignDetails.couponCodeFormat || "",
                linkCoupon: campaignDetails.linkCoupon || "",
                linkToUseCoupon: campaignDetails.linkToUseCoupon || "",
                discountValue: campaignDetails.discountValue || "",
                couponRedemptionInstructions: campaignDetails.couponRedemptionInstructions || "",
                sponsor: sponsorId || "",
                brand: campaignDetails.brand || "",
                productServices: campaignDetails.productIdsArray || campaignDetails.productServices || [], // ✅ Check both fields for backward compatibility
                redemptionType: campaignDetails.redemptionType || "",
                apiType: campaignDetails.typeOfApi || "", // Map typeOfApi back to apiType for form
                apiEndpoint: campaignDetails.apiEndpoint || "",
                apiHeaders: (() => {
                    let headers = campaignDetails.headers || "";
                    if (headers && campaignDetails.uniqueId) {
                        try {
                            const parsed = typeof headers === 'string' ? JSON.parse(headers) : headers;
                            if (!parsed["client-secret"] || parsed["client-secret"] === "{CampaignClientSecret}") {
                                parsed["client-secret"] = campaignDetails.uniqueId;
                                return JSON.stringify(parsed, null, 2);
                            }
                        } catch (e) { /* keep original */ }
                    }
                    return headers;
                })(), // Map headers back to apiHeaders for form, auto-fill client-secret with uniqueId
                apiBody: campaignDetails.apiBody || "",
                webhookUrl: campaignDetails.webhookUrl || "",
                dailyRunTime: campaignDetails.dailyRunTime || [],
                rewardType: campaignDetails.rewardType || "",
                productIdsArray: campaignDetails.productIdsArray || [],
                // Sponsor a Blog specific fields
                blogId: campaignDetails.blogId?._id || campaignDetails.blogId || "",
                link: campaignDetails.link || "",
                totalPageViews: campaignDetails.totalPageViews || "",
                ctr: campaignDetails.ctr || "",
                comments: campaignDetails.comments || "",
                likes: campaignDetails.likes || "",
                // Promote Brand/Product specific fields
                promotionType: campaignDetails.promotionType ? campaignDetails.promotionType.toLowerCase() : "",
                promotionalLink: campaignDetails.promotionalLink || "",
                // Promote an Event specific fields
                totalNumberOfVolunteers: campaignDetails.totalNumberOfVolunteers || "",
                totalHoursOfVolunteering: campaignDetails.totalHoursOfVolunteering || "",
                eventId: campaignDetails.eventId?._id || campaignDetails.eventId || "",
                certificateAvailable: campaignDetails.certificateAvailable || "",
                certificateTemplate: campaignDetails.certificateTemplate || "",
                certificateTemplateMedia: campaignDetails.certificateTemplateMedia || null,
                actualCertificateMedia: campaignDetails.actualCertificateMedia || null,
                // Advertisement campaign type fields
                advertisement: campaignDetails.advertisement || "",
                advertisementSlot: campaignDetails.advertisementSlot || "",
                kpi: Array.isArray(campaignDetails.kpi) ? campaignDetails.kpi : [],
                kpiNames: Array.isArray(campaignDetails.kpi) ? campaignDetails.kpi : [],
                // Plant Trees campaign type fields
                plantationProject: campaignDetails.plantationProject || "",
                projectName: campaignDetails.projectName || "",
                projectLocation: campaignDetails.projectLocation || "",
                projectAddress: campaignDetails.projectAddress || "",
                projectShortDescription: campaignDetails.projectShortDescription || "",
                projectLongDescription: campaignDetails.projectLongDescription || "",
                projectDate: campaignDetails.projectDate || "",
                projectPhotos: campaignDetails.projectPhotos || [],
                spendPointsPerTree: campaignDetails.spendPointsPerTree || 0,
                maxCertificateCount: campaignDetails.maxCertificateCount || 0,
                // Common field for all campaign types
                notes: campaignDetails.notes || "",
                sponsorMessage: campaignDetails.sponsorMessage || "",
            });

            setActive(campaignDetails.active || false);
            setCoverImage(campaignDetails.imageUrl || null);
            setCouponMedia(campaignDetails.couponMedia || campaignDetails.sponsoredImage || null);
            setShowInHomePage(campaignDetails.showInHomePage || false);

            // Set category for challenge filtering
            const challengeCategoryId = (typeof campaignDetails.challengeId === 'object' && campaignDetails.challengeId?.category?._id) ||
                (typeof campaignDetails.challengeId === 'object' && campaignDetails.challengeId?.category) ||
                "";
            setSelectedCategoryId(challengeCategoryId);

            setTimeout(() => {
                if (campaignDetails.campaignType === 'Plant Trees' && campaignDetails.plantationProject) {
                    const selectedProject = projectsList?.find(p => p._id === campaignDetails.plantationProject);
                    console.log('selected project: ', selectedProject);
                    reset(prev => ({
                        ...prev,
                        plantationProject: selectedProject?._id || "",
                        projectName: selectedProject?.name || "",
                        projectLocation: selectedProject?.location || "",
                        projectAddress: selectedProject?.address || "",
                        projectShortDescription: selectedProject?.shortDescription || "",
                        projectLongDescription: selectedProject?.longDescription || "",
                        projectDate: selectedProject?.eventDateTime ? formatDateForInput(selectedProject.eventDateTime) : "",
                        projectPhotos: selectedProject?.photos || [],
                    }));
                }
            }, 100);
        }
    }, [campaignDetails, reset]);

    if (isBlogListLoading || isProductsLoading || isEventListLoading || isBrandsLoading || isAdsLoading || isSlotsLoading || isProjectsLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <form
            onSubmit={(e) => {
                console.log('Form submit event triggered!'); // Debug log
                console.log('Form is valid:', Object.keys(errors).length === 0); // Debug log
                console.log('All errors:', errors); // Debug log
                handleSubmit(onSubmit, (errors) => {
                    // Show validation errors in toasts
                    Object.keys(errors).forEach(key => {
                        toast.error(errors[key].message);
                    });
                })(e);
            }}
            className="flex flex-col font-semibold text-gray-900 rounded-none text-base  gap-6"
        >
            <div className='flex justify-end items-center'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                />
            </div>
            <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                <SubHeading text={"Campaign Information"} className="mb-4" />
                <div className="flex flex-col shrink-0 basis-0  w-full">
                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize"><span className="text-red text-xs inline">*</span> Campaign Name</label>
                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                        <input
                            type="text"
                            placeholder="Enter Campaign Name"
                            {...register("title", { required: "Campaign Name is required" })}
                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.title ? "border-red-500" : ""}`}
                        />
                    </div>
                    {errors.title && <p className="text-red text-sm">{errors.title.message}</p>}
                </div>
                <div className='flex flex-row-reverse items-center gap-3 mt-3'>
                    <div className='flex flex-col gap-1 basis-1/2'>
                        <label htmlFor="sponsorName" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                            <span className="text-red text-xs inline">* </span>
                            Client
                        </label>
                        <NewDropdown
                            name={"sponsor"}
                            dataList={organiserList}
                            placeholder={'Select Client'}
                            value={watchedSponsor}
                            onChange={(e) => {
                                setValue("sponsor", e.value);
                                setValue("productServices", []); // Reset products when client changes
                                if (e.value) {
                                    trigger("sponsor");
                                }
                            }}
                        />
                        {errors.sponsor && <p className="text-red text-sm mt-1">{errors.sponsor.message}</p>}
                    </div>
                    <div className='flex flex-col gap-0.5 basis-1/2'>
                        <label className='text-[14px] font-[600] text-gray-900'><span className="text-red text-xs inline">*</span> Campaign Type</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md  text-slate-400">
                            <NewDropdown
                                name={"Campaign Type"}
                                dataList={[
                                    { value: 'Sponsor a Challenge', label: 'Sponsor a Challenge' },
                                    { value: 'Sponsor a Blog', label: 'Sponsor a Blog' },
                                    { value: 'Promote Brand/Product', label: 'Promote Brand/Product' },
                                    { value: 'Plant Trees', label: 'Plant Trees' },
                                    { value: 'Promote an Event', label: 'Promote an Event' },
                                    { value: 'Advertisement', label: 'Advertisement' },
                                ]}
                                placeholder={'Select Campaign Type'}
                                value={watchedCampaignType}
                                onChange={(e) => {
                                    setValue("campaignType", e.value);
                                    if (e.value) {
                                        trigger("campaignType");
                                        // Also trigger validation for start/end dates if campaign type is "Sponsor a Challenge"
                                        if (e.value === 'Sponsor a Challenge') {
                                            trigger("startDate");
                                            trigger("endDate");
                                        }
                                    }
                                }}
                            />
                        </div>
                        {errors.campaignType && <p className="text-red text-sm mt-1">{errors.campaignType.message}</p>}
                    </div>
                </div>
                <div className='flex flex-col gap-3 mt-3'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <div className='flex flex-col gap-1'>
                            <label className='text-[14px] font-[600] text-gray-900'>
                                {
                                    (watchedCampaignType === 'Sponsor a Challenge' || watchedCampaignType === 'Promote Brand/Product') &&
                                    <span className="text-red text-xs inline">*</span>
                                }
                                Campaign Start
                            </label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="datetime-local"
                                    placeholder="Enter Start Date"
                                    {...register("startDate", {
                                        required: (watchedCampaignType === 'Sponsor a Challenge' || watchedCampaignType === 'Promote Brand/Product') ? "Campaign Start Date is required" : false
                                    })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.startDate ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.startDate && <p className="text-red text-sm">{errors.startDate.message}</p>}
                        </div>
                        <div className='flex flex-col gap-1'>
                            <label className='text-[14px] font-[600] text-gray-900'>
                                {
                                    (watchedCampaignType === 'Sponsor a Challenge' || watchedCampaignType === 'Promote Brand/Product') &&
                                    <span className="text-red text-xs inline">*</span>
                                }
                                Campaign End</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="datetime-local"
                                    placeholder="Enter End Date"
                                    {...register("endDate", {
                                        required: (watchedCampaignType === 'Sponsor a Challenge' || watchedCampaignType === 'Promote Brand/Product') ? "Campaign End Date is required" : false
                                    })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.endDate ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.endDate && <p className="text-red text-sm">{errors.endDate.message}</p>}
                        </div>
                    </div>
                </div>
                {/* <div className="flex gap-3 mt-3">
                    <div className="flex flex-col w-full gap-6 ">
                        Description
                        <div className="flex flex-col grow shrink-0 basis-0 min-h-[150px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">Campaign Description (to be displayed on the partner dashboard)</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md text-slate-400">
                                <RichTextEditor
                                    placeholder="Write your blog overview here..."
                                    name="overview"
                                    value={getValues("overview")}
                                    onChange={(value) => setValue("overview", value)}
                                    error={errors.overview}
                                />
                            </div>
                            {errors.description && <p className="text-red text-sm">{errors.description.message}</p>}
                        </div>
                    </div>
                    Cover Image
                    <div className="">
                        <MediaUpload
                            className={`h-auto`}
                            clearMedia={mediaClear}
                            label={"Campaign Logo (Overrides Sponsor Logo)"}
                            existingImage={coverImage}
                            setImageUpload={setCoverImage}
                            stateImage={coverImage}
                            setStateImageUpload={setCoverImage}
                        />
                    </div>
                </div> */}
            </div>
            {/* Budget */}
            {
                watchedCampaignType !== 'Plant Trees' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-4">
                        <SubHeading text={"Budget & Pricing"} />
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                            {/* Pricing Model */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Pricing Model</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md  text-slate-400">
                                    <NewDropdown
                                        name={"pricingModel"}
                                        dataList={(() => {
                                            if (watchedCampaignType === 'Sponsor a Challenge') {
                                                return [
                                                    { value: 'Cost per Redemption', label: 'Cost per Redemption' },
                                                ];
                                            } else if (watchedCampaignType === 'Sponsor a Blog') {
                                                return [
                                                    { value: 'Flat Rate', label: 'Flat Rate' },
                                                    { value: 'Cost per View', label: 'Cost per View' },
                                                    { value: 'Cost per engagement', label: 'Cost per engagement' },
                                                ];
                                            } else if (watchedCampaignType === 'Promote Brand/Product') {
                                                return [
                                                    { value: 'Cost per Redemption', label: 'Cost per Redemption' },
                                                    { value: 'Flat Rate', label: 'Flat Rate' },
                                                ];
                                            } else if (watchedCampaignType === 'Plant Trees') {
                                                return [
                                                    { value: 'Cost per Tree', label: 'Cost per Tree' },
                                                    { value: 'Flat Rate', label: 'Flat Rate' },
                                                ];
                                            } else if (watchedCampaignType === 'Promote an Event') {
                                                return [
                                                    { value: 'Cost per Volunteer', label: 'Cost per Volunteer' },
                                                    { value: 'Flat Rate', label: 'Flat Rate' },
                                                ];
                                            } else if (watchedCampaignType === 'Advertisement') {
                                                return [
                                                    { value: 'CPM', label: 'CPM (Cost per Mille)' },
                                                    { value: 'CPC', label: 'CPC (Cost per Click)' },
                                                    { value: 'Flat Rate', label: 'Flat Rate' },
                                                ];
                                            } else {
                                                return [
                                                    { value: '', label: 'Select Campaign Type First' },
                                                ];
                                            }
                                        })()}
                                        placeholder={'Choose Pricing Model'}
                                        value={watchedPricingModel}
                                        onChange={(e) => setValue("pricingModel", e.value)}
                                    />
                                </div>
                            </div>
                            {/* Rate Type */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Rate Type</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                    <NewDropdown
                                        name={"campaignRateType"}
                                        dataList={[
                                            { value: 'Fixed', label: 'Fixed' },
                                            { value: 'Percentage', label: 'Percentage' },
                                        ]}
                                        placeholder={'Choose Rate Type'}
                                        value={watchedCampaignRateType}
                                        onChange={(e) => setValue("campaignRateType", e.value)}
                                    />
                                </div>
                                {/* {errors.campaignBudget && <p className="text-red text-sm">{errors.campaignBudget.message}</p>} */}
                            </div>
                            {/* Rate Value */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Rate Value</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Rate Value"
                                        {...register("rateValue", { required: false })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                    />
                                </div>
                            </div>
                            {/* Payment Terms */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Payment Terms</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                    <NewDropdown
                                        name={"paymentTerms"}
                                        dataList={[
                                            { value: 'Net 15', label: 'Net 15' },
                                            { value: 'Net 30', label: 'Net 30' },
                                            { value: 'Immediate', label: 'Immediate' },
                                        ]}
                                        placeholder={'Payment Terms'}
                                        value={watchedPaymentTerms}
                                        onChange={(e) => setValue("paymentTerms", e.value)}
                                    />
                                </div>
                                {errors.campaignBudget && <p className="text-red text-sm">{errors.campaignBudget.message}</p>}
                            </div>
                            {/* Budget */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Campaign Budget (in INR)</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="number"
                                        placeholder="Enter Campaign Budget"
                                        {...register("campaignBudget", { required: false })}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.campaignBudget ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.campaignBudget && <p className="text-red text-sm">{errors.campaignBudget.message}</p>}
                            </div>
                            {/* Invoice Frequency */}
                            <div className='flex flex-col gap-2'>
                                <label className='text-[14px] font-[600] text-gray-900'>Invoice Frequency</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                    <NewDropdown
                                        name={"invoiceFrequency"}
                                        dataList={[
                                            { value: 'Weekly', label: 'Weekly' },
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'Quarterly', label: 'Quarterly' },
                                        ]}
                                        placeholder={'Invoice Frequency'}
                                        value={watchedInvoiceFrequency}
                                        onChange={(e) => setValue("invoiceFrequency", e.value)}
                                    />
                                </div>
                                {errors.campaignBudget && <p className="text-red text-sm">{errors.campaignBudget.message}</p>}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* KPI */}
            {
                (watchedCampaignType === 'Sponsor a Challenge' || watchedCampaignType === 'Promote Brand/Product') && (

                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"KPIs and Reporting"} />
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/*Multiselect KPIs*/}
                                <div className='flex flex-col gap-2 col-span-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select KPIs</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                        <NewDropdown
                                            name={"KPIs"}
                                            dataList={[
                                                { value: "TotalCarbonSavings", label: "Total Carbon Savings" },
                                                { value: "TotalRevenueGenerated", label: "Total Revenue Generated" },
                                                { value: "ConversionRate", label: "Conversion Rate" },
                                            ]}
                                            allowMultiple={true}
                                            placeholder={'KPIs'}
                                            value={watchedKpiNames}
                                            onChange={(e) => setValue("kpiNames", Array.isArray(e.value) ? e.value : [])}
                                        />
                                    </div>
                                </div>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign Report Frequency</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                        <NewDropdown
                                            name={"Campaign Report Frequency"}
                                            dataList={[
                                                { value: "Weekly", label: "Weekly" },
                                                { value: "Monthly", label: "Monthly" },
                                                { value: "Quarterly", label: "Quarterly" },
                                            ]}
                                            placeholder={'Campaign Report Frequency'}
                                            value={watchedReportFrequency}
                                            onChange={(e) => setValue("reportFrequency", e.value)}
                                        />
                                    </div>
                                </div>



                                {/* KPI */}
                                {/* <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Key KPI For Campaign</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md  text-slate-400">
                                        <NewDropdown
                                            name={"kpi"}
                                            dataList={[
                                                { value: 'CTR', label: 'Click Through Rate - CTR' },
                                                { value: 'CTC', label: 'Cost To Click - CTC' },
                                            ]}
                                            placeholder={'Click Through Rate-CTR, Cost To Click-CTC'}
                                            onChange={(e) => setValue("kpi", e.value)}
                                        />
                                    </div>
                                </div> */}

                                {/* Report Recipient Email */}
                                <div className={`flex flex-col w-full col-span-3`}>
                                    <label className='text-[14px] font-[600] text-gray-900'>Report Recipient Email (Comma separated)</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Report Recipient Email (Comma separated)"
                                            {...register("reportRecipientEmail", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.reportRecipientEmail && <p className="text-red text-sm">{errors.reportRecipientEmail.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                watchedCampaignType === 'Sponsor a Blog' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"KPIs and Reporting"} />
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Total Page Views */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Total Page Views</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Total Page Views"
                                            {...register("totalPageViews", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.totalPageViews ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.totalPageViews && <p className="text-red text-sm">{errors.totalPageViews.message}</p>}
                                </div>
                                {/* CTR */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>CTR</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter CTR"
                                            {...register("ctr", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.ctr ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.ctr && <p className="text-red text-sm">{errors.ctr.message}</p>}
                                </div>
                                {/* KPI */}
                                {/* <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Key KPI For Campaign</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md  text-slate-400">
                                        <NewDropdown
                                            name={"kpi"}
                                            dataList={[
                                                { value: 'CTR', label: 'Click Through Rate - CTR' },
                                                { value: 'CTC', label: 'Cost To Click - CTC' },
                                            ]}
                                            placeholder={'Click Through Rate-CTR, Cost To Click-CTC'}
                                            onChange={(e) => setValue("kpi", e.value)}
                                        />
                                    </div>
                                </div> */}
                                {/* Report Frequency */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign Report Frequency (Week)</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'End of Campaign', label: 'End of Campaign' },
                                        ]}
                                        name={"reportFrequency"}
                                        value={watchedReportFrequency}
                                        placeholder={'Select Report Frequency'}
                                        onChange={(e) => {
                                            setValue("reportFrequency", e.value);
                                            if (e.value) {
                                                trigger("reportFrequency");
                                            }
                                        }}
                                    />
                                    {errors.reportFrequency && <p className="text-red text-sm">{errors.reportFrequency.message}</p>}
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Comments */}
                                <div className='flex flex-col col-span-1'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Comments</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Comments"
                                            {...register("comments", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.comments ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.comments && <p className="text-red text-sm">{errors.comments.message}</p>}
                                </div>
                                {/* Likes */}
                                <div className='flex flex-col col-span-1'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Likes</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Likes"
                                            {...register("likes", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.likes ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.likes && <p className="text-red text-sm">{errors.likes.message}</p>}
                                </div>
                                {/* Report Recipient Email */}
                                <div className='flex flex-col w-full'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Report Recipient Email (Comma separated)</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Report Recipient Email (Comma separated)"
                                            {...register("reportRecipientEmail", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.reportRecipientEmail && <p className="text-red text-sm">{errors.reportRecipientEmail.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                watchedCampaignType === 'Plant Trees' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"KPIs and Reporting"} />
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/*Multiselect KPIs*/}
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select KPIs</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border-navBtn text-slate-400">
                                        <NewDropdown
                                            name={"KPIs"}
                                            dataList={[
                                                { value: "CTR", label: "CTR" },
                                                { value: "TotalTreesPlanted", label: "Total Trees Planted" },
                                                { value: "TotalVolunteers", label: "Total Volunteers" },
                                                { value: "TotalCertificateIssued", label: "Total Certificate Issued" },
                                            ]}
                                            allowMultiple={true}
                                            placeholder={'KPIs'}
                                            value={watchedKpi}
                                            onChange={(e) => {
                                                setValue("kpi", Array.isArray(e.value) ? e.value : [e.value]);
                                                if (e.value) {
                                                    trigger("kpi");
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* Report Frequency */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign Report Frequency (Week)</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'End of Campaign', label: 'End of Campaign' },
                                        ]}
                                        name={"reportFrequency"}
                                        placeholder={'Select Report Frequency'}
                                        value={watchedReportFrequency}
                                        onChange={(e) => {
                                            setValue("reportFrequency", e.value);
                                            if (e.value) {
                                                trigger("reportFrequency");
                                            }
                                        }}
                                    />
                                    {errors.reportFrequency && <p className="text-red text-sm">{errors.reportFrequency.message}</p>}
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Report Recipient Email */}
                                <div className='flex flex-col w-full col-span-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Report Recipient Email (Comma separated)</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Report Recipient Email (Comma separated)"
                                            {...register("reportRecipientEmail", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.reportRecipientEmail && <p className="text-red text-sm">{errors.reportRecipientEmail.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                watchedCampaignType === 'Promote an Event' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"KPIs and Reporting"} />
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Total Number of volunteers */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Total Number of volunteers</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Total Number of volunteers"
                                            {...register("totalNumberOfVolunteers", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.totalNumberOfVolunteers ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.totalNumberOfVolunteers && <p className="text-red text-sm">{errors.totalNumberOfVolunteers.message}</p>}
                                </div>
                                {/* Total hours of volunteering */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Total hours of volunteering</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Total hours of volunteering"
                                            {...register("totalHoursOfVolunteering", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.totalHoursOfVolunteering ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.totalHoursOfVolunteering && <p className="text-red text-sm">{errors.totalHoursOfVolunteering.message}</p>}
                                </div>
                                {/* KPI */}
                                {/* <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Key KPI For Campaign</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md  text-slate-400">
                                        <NewDropdown
                                            name={"kpi"}
                                            dataList={[
                                                { value: 'CTR', label: 'Click Through Rate - CTR' },
                                                { value: 'CTC', label: 'Cost To Click - CTC' },
                                            ]}
                                            placeholder={'Click Through Rate-CTR, Cost To Click-CTC'}
                                            onChange={(e) => setValue("kpi", e.value)}
                                        />
                                    </div>
                                </div> */}
                                {/* Report Frequency */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign Report Frequency (Week)</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'End of Campaign', label: 'End of Campaign' },
                                        ]}
                                        name={"reportFrequency"}
                                        placeholder={'Select Report Frequency'}
                                        onChange={(e) => {
                                            setValue("reportFrequency", e.value);
                                            if (e.value) {
                                                trigger("reportFrequency");
                                            }
                                        }}
                                    />
                                    {errors.reportFrequency && <p className="text-red text-sm">{errors.reportFrequency.message}</p>}
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Total Certificates Issued */}
                                {/* <div className='flex flex-col col-span-1'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Total Certificates Issued</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Total Certificates Issued"
                                            {...register("totalCertificatesIssued", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.totalCertificatesIssued ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.totalCertificatesIssued && <p className="text-red text-sm">{errors.totalCertificatesIssued.message}</p>}
                                </div> */}
                                {/* Report Recipient Email */}
                                <div className='flex flex-col w-full col-span-3'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Report Recipient Email (Comma separated)</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Report Recipient Email (Comma separated)"
                                            {...register("reportRecipientEmail", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.reportRecipientEmail && <p className="text-red text-sm">{errors.reportRecipientEmail.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                watchedCampaignType === 'Advertisement' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"KPIs and Reporting"} />
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* KPI */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign KPI (Week)</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'CTR', label: 'CTR' },
                                        ]}
                                        name={"kpi"}
                                        placeholder={'Select KPI'}
                                        allowMultiple={true}
                                        value={watchedKpi}
                                        onChange={(e) => {
                                            setValue("kpi", Array.isArray(e.value) ? e.value : [e.value]);
                                            if (e.value) {
                                                trigger("kpi");
                                            }
                                        }}
                                    />
                                    {errors.kpi && <p className="text-red text-sm">{errors.kpi.message}</p>}
                                </div>
                                {/* Report Frequency */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Campaign Report Frequency (Week)</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'End of Campaign', label: 'End of Campaign' },
                                        ]}
                                        name={"reportFrequency"}
                                        placeholder={'Select Report Frequency'}
                                        value={watchedReportFrequency}
                                        onChange={(e) => {
                                            setValue("reportFrequency", e.value);
                                            if (e.value) {
                                                trigger("reportFrequency");
                                            }
                                        }}
                                    />
                                    {errors.reportFrequency && <p className="text-red text-sm">{errors.reportFrequency.message}</p>}
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Total Certificates Issued */}
                                {/* <div className='flex flex-col col-span-1'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Total Certificates Issued</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Total Certificates Issued"
                                            {...register("totalCertificatesIssued", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.totalCertificatesIssued ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.totalCertificatesIssued && <p className="text-red text-sm">{errors.totalCertificatesIssued.message}</p>}
                                </div> */}
                                {/* Report Recipient Email */}
                                <div className='flex flex-col w-full col-span-3'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Report Recipient Email (Comma separated)</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Report Recipient Email (Comma separated)"
                                            {...register("reportRecipientEmail", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded-md text-base ${errors.reportRecipientEmail ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.reportRecipientEmail && <p className="text-red text-sm">{errors.reportRecipientEmail.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* {
                watchedCampaignType === 'Promote an Event' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"Advertisement"} />
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                Advertisement
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select Advertisement</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Banner Ad', label: 'Banner Advertisement' },
                                            { value: 'Video Ad', label: 'Video Advertisement' },
                                            { value: 'Native Ad', label: 'Native Advertisement' },
                                        ]}
                                        name={"advertisement"}
                                        placeholder={'Select Advertisement'}
                                        onChange={(e) => setValue("advertisement", e.value)}
                                    />
                                    {errors.advertisement && <p className="text-red text-sm">{errors.advertisement.message}</p>}
                                </div>
                                Ad Slot
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select Ad Slot</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Home Page', label: 'Home Page' },
                                            { value: 'Challenge Home Page', label: 'Challenge Home Page' },
                                            { value: 'Blogs', label: 'Blogs' },
                                            { value: 'Knowledge Board', label: 'Knowledge Board' },
                                        ]}
                                        name={"adSlot"}
                                        placeholder={'Select Ad Slot'}
                                        onChange={(e) => setValue("adSlot", e.value)}
                                    />
                                    {errors.adSlot && <p className="text-red text-sm">{errors.adSlot.message}</p>}
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                CC Ad KPI
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select CC Ad KPI</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'CTR', label: 'CTR' },
                                        ]}
                                        name={"CCAdKPI"}
                                        placeholder={'Select CC Ad KPI'}
                                        onChange={(e) => setValue("CCAdKPI", e.value)}
                                    />
                                    {errors.CCAdKPI && <p className="text-red text-sm">{errors.CCAdKPI.message}</p>}
                                </div>
                                Ad Reporting Frequency
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Select Ad Reporting Frequency</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Monthly', label: 'Monthly' },
                                            { value: 'End of Campaign', label: 'End of Campaign' },
                                        ]}
                                        name={"adReportingFrequency"}
                                        placeholder={'Select Ad Reporting Frequency'}
                                        onChange={(e) => setValue("adReportingFrequency", e.value)}
                                    />
                                    {errors.adReportingFrequency && <p className="text-red text-sm">{errors.adReportingFrequency.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Promotion Type Section for Promote Brand/Product campaigns */}
            {
                watchedCampaignType === 'Promote Brand/Product' && (
                    <div className='flex flex-col mt-0 gap-4'>
                        <div className="flex flex-col px-5 py-4 gap-3 basis-1/3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                            <SubHeading text={"Promotion Configuration"} />
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                {/* Promotion Type */}
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                        {active && <span className="text-red text-xs inline">* </span>}
                                        Promotion Type
                                    </label>
                                    <NewDropdown
                                        name={"Promotion Type"}
                                        dataList={[
                                            { value: "brand", label: "Brand" },
                                            { value: "product", label: "Product" },
                                        ]}
                                        placeholder={'Select Promotion Type'}
                                        value={watchedPromotionType}
                                        onChange={(e) => setValue("promotionType", e.value)}
                                    />
                                    {errors.promotionType && <p className="text-red text-sm mt-1">{errors.promotionType.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Dynamic Component based on campaign type */}
            {/* Link A Challenge */}
            {
                watchedCampaignType === 'Sponsor a Challenge' && (
                    <>
                        <div className='flex flex-col mt-0 gap-4'>
                            <div className="flex flex-col px-5 py-4 gap-3 basis-1/3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                <SubHeading text={"Rewards"} />
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                    {/* Challenge category */}
                                    <div className='flex flex-col gap-2 col-span-1'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                            {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                            Reward Type
                                        </label>
                                        <NewDropdown
                                            name={"Reward Type"}
                                            dataList={[
                                                { value: "DiscountCoupon", label: "Discount Coupon" },
                                            ]}
                                            placeholder={'Select Reward Type'}
                                            value={watchedRewardType}
                                            onChange={(e) => setValue("rewardType", e.value)}
                                        />
                                        {errors.rewardType && <p className="text-red text-sm mt-1">{errors.rewardType.message}</p>}
                                    </div>


                                    {

                                        watchedRewardType === 'DiscountCoupon' && (
                                            <>
                                                {/* Type of discount */}
                                                {/* <div className="flex flex-col gap-3 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Type of Discount
                                                    </label>
                                                    <NewDropdown
                                                        name={"typeOfDiscount"}
                                                        dataList={[
                                                            { value: 'Percentage Discount', label: 'Percentage Discount' },
                                                            { value: 'Flat Amount', label: 'Flat Amount' },
                                                        ]}
                                                        placeholder={'Select Type of Discount'}
                                                        value={watchedTypeOfDiscount}
                                                        onChange={(e) => setValue("typeOfDiscount", e.value)}
                                                    />
                                                    {errors.typeOfDiscount && <p className="text-red text-sm mt-1">{errors.typeOfDiscount.message}</p>}
                                                </div> */}
                                                {/* Discount Value */}
                                                {/* <div className="flex flex-col gap-2 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Discount Value
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Select Discount Value"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("discountValue", { 
                                                                required: watchedCampaignType === 'Sponsor a Challenge' && active ? "Discount Value is required" : false 
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.discountValue && <p className="text-red text-sm mt-1">{errors.discountValue.message}</p>}
                                                </div> */}
                                                {/* Coupon Code Format */}
                                                <div className='flex flex-col gap-2 basis-1/2'>
                                                    <label className='text-[14px] font-[600] text-gray-900'>
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Coupon Code Format
                                                    </label>
                                                    <NewDropdown
                                                        name={"couponCodeFormat"}
                                                        dataList={[
                                                            { value: 'unique', label: 'Unique' },
                                                            { value: 'universal', label: 'Universal' },
                                                        ]}
                                                        placeholder={'Select Coupon Code Format'}
                                                        value={watchedCouponCodeFormat}
                                                        onChange={(e) => setValue("couponCodeFormat", e.value)}
                                                    />
                                                    {errors.couponCodeFormat && <p className="text-red text-sm mt-1">{errors.couponCodeFormat.message}</p>}
                                                </div>
                                                {/* Brand Dropdown - Optional */}
                                                <div className='flex flex-col gap-2 basis-1/2'>
                                                    <label className='text-[14px] font-[600] text-gray-900'>
                                                        {watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' && active && <span className="text-red text-xs inline">* </span>}
                                                        Brand {watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' ? '(Filter for Products)' : watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' ? '' : '(Optional)'}
                                                    </label>
                                                    <NewDropdown
                                                        name={"brand"}
                                                        dataList={brandDropDown || []}
                                                        placeholder={watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' ? 'Select Brand to Filter Products' : watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' ? 'Select Brand' : 'Select Brand (Optional)'}
                                                        value={watchedBrand}
                                                        onChange={(e) => {
                                                            setValue("brand", e.value);
                                                            setValue("productServices", []); // Reset product services when brand changes
                                                        }}
                                                    />
                                                    {errors.brand && <p className="text-red text-sm mt-1">{errors.brand.message}</p>}
                                                </div>
                                                {/* Product/Services MultiSelect Dropdown */}
                                                {!(watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand') && (
                                                    <div className='flex flex-col gap-2 basis-1/2'>
                                                        <label className='text-[14px] font-[600] text-gray-900'>
                                                            {((watchedCampaignType === 'Sponsor a Challenge' && active) || (watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' && active)) && <span className="text-red text-xs inline">* </span>}
                                                            Product/Services
                                                        </label>
                                                        <NewDropdown
                                                            name={"productServices"}
                                                            dataList={getFilteredProducts()}
                                                            allowMultiple={true}
                                                            placeholder={'Select Product/Services'}
                                                            value={watchedProductServices}
                                                            onChange={(e) => setValue("productServices", Array.isArray(e.value) ? e.value : [])}
                                                        />
                                                        {errors.productServices && <p className="text-red text-sm mt-1">{errors.productServices.message}</p>}
                                                    </div>
                                                )}
                                                {/* Link to Use Coupon */}
                                                <div className="flex flex-col gap-2 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && !watchedCouponRedemptionInstructions && <span className="text-red text-xs inline">* </span>}
                                                        Link to Use Coupon
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Link to Use Coupon"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("linkToUseCoupon", { required: false })}
                                                        />
                                                    </div>
                                                    {errors.linkToUseCoupon && <p className="text-red text-sm mt-1">{errors.linkToUseCoupon.message}</p>}
                                                </div>
                                                {/* Coupon Redemption Instructions */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && !getValues("linkToUseCoupon") && <span className="text-red text-xs inline">* </span>}
                                                        Coupon Redemption Instructions
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <textarea
                                                            placeholder="Enter detailed instructions on how users can redeem this coupon..."
                                                            rows={3}
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary resize-y min-h-[80px]"
                                                            {...register("couponRedemptionInstructions", { required: false })}
                                                        />
                                                    </div>
                                                    {errors.couponRedemptionInstructions && <p className="text-red text-sm mt-1">{errors.couponRedemptionInstructions.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active
                                                            ? "Either provide redemption instructions OR link to use coupon (at least one is required)"
                                                            : "Provide step-by-step instructions for users on how to redeem the coupon"
                                                        }
                                                    </p>
                                                </div>
                                            </>
                                        )
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Redemption Tracking Section */}
                        {watchedRewardType === 'DiscountCoupon' && (
                            <div className='flex flex-col mt-0 gap-4'>
                                <div className="flex flex-col px-5 py-4 gap-3 basis-1/3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                    <SubHeading text={"Redemption Tracking"} />
                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                        {/* Redemption Type */}
                                        <div className='flex flex-col gap-2 col-span-1'>
                                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                                {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                Redemption Type
                                            </label>
                                            <NewDropdown
                                                name={"redemptionType"}
                                                dataList={[
                                                    { value: "WebHook", label: "WebHook" },
                                                    { value: "PullApi", label: "Pull Api" },
                                                    { value: "NoTracking", label: "No Tracking" },
                                                ]}
                                                placeholder={'Select Redemption Type'}
                                                value={watchedRedemptionType}
                                                onChange={(e) => setValue("redemptionType", e.value)}
                                            />
                                            {errors.redemptionType && <p className="text-red text-sm mt-1">{errors.redemptionType.message}</p>}
                                        </div>

                                        {/* Pull API Fields - Only show if redemption type is Pull Api */}
                                        {watchedRedemptionType === 'PullApi' && (
                                            <>
                                                {/* Type of API */}
                                                <div className='flex flex-col gap-2 col-span-1'>
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Type of API
                                                    </label>
                                                    <NewDropdown
                                                        name={"apiType"}
                                                        dataList={[
                                                            { value: "GET", label: "GET" },
                                                            { value: "POST", label: "POST" },
                                                        ]}
                                                        placeholder={'Select API Type'}
                                                        value={watchedApiType}
                                                        onChange={(e) => setValue("apiType", e.value)}
                                                    />
                                                    {errors.apiType && <p className="text-red text-sm mt-1">{errors.apiType.message}</p>}
                                                </div>

                                                {/* API Endpoint */}
                                                <div className="flex flex-col gap-2 col-span-1">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        API Endpoint
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter API Endpoint"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("apiEndpoint", {
                                                                required: watchedCampaignType === 'Sponsor a Challenge' && active && watchedRedemptionType === 'PullApi' ? "API Endpoint is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.apiEndpoint && <p className="text-red text-sm mt-1">{errors.apiEndpoint.message}</p>}
                                                </div>

                                                {/* Headers */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Headers (JSON format)
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <textarea
                                                            placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary min-h-[80px]"
                                                            {...register("apiHeaders", {
                                                                required: watchedCampaignType === 'Sponsor a Challenge' && active && watchedRedemptionType === 'PullApi' ? "API Headers is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.apiHeaders && <p className="text-red text-sm mt-1">{errors.apiHeaders.message}</p>}
                                                </div>

                                                {/* Body JSON - Only show if API type is POST */}
                                                {watchedApiType === 'POST' && (
                                                    <div className="flex flex-col gap-2 col-span-3">
                                                        <label className="text-[14px] font-[600] text-gray-900">
                                                            {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                            Body JSON (with replaceable variables)
                                                        </label>
                                                        <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                            <textarea
                                                                placeholder='{"couponCode": "{{couponCode}}", "userId": "{{userId}}", "amount": "{{amount}}"}'
                                                                className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary min-h-[120px]"
                                                                {...register("apiBody", {
                                                                    required: watchedCampaignType === 'Sponsor a Challenge' && active && watchedRedemptionType === 'PullApi' && watchedApiType === 'POST' ? "API Body is required" : false
                                                                })}
                                                            />
                                                        </div>
                                                        {errors.apiBody && <p className="text-red text-sm mt-1">{errors.apiBody.message}</p>}
                                                        <p className="text-xs text-gray-500">
                                                            Use double curly braces for replaceable variables: {`{{variableName}}`}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Daily Run Time - Always show for Pull API */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Daily Run Time (Select hours when the API should run)
                                                    </label>
                                                    <NewDropdown
                                                        name={"dailyRunTime"}
                                                        dataList={getTimeSlots()}
                                                        allowMultiple={true}
                                                        placeholder={'Select Daily Run Time Hours'}
                                                        value={watchedDailyRunTime}
                                                        onChange={(e) => setValue("dailyRunTime", Array.isArray(e.value) ? e.value : [])}
                                                    />
                                                    {errors.dailyRunTime && <p className="text-red text-sm mt-1">{errors.dailyRunTime.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        Select one or more hours when the API should be called daily
                                                    </p>
                                                </div>
                                            </>
                                        )}

                                        {/* WebHook Fields - Only show if redemption type is WebHook */}
                                        {watchedRedemptionType === 'WebHook' && (
                                            <>
                                                {/* Webhook URL */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Sponsor a Challenge' && active && <span className="text-red text-xs inline">* </span>}
                                                        Webhook URL
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="url"
                                                            placeholder="https://example.com/webhook/coupon-redeemed"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("webhookUrl", {
                                                                required: watchedCampaignType === 'Sponsor a Challenge' && active && watchedRedemptionType === 'WebHook' ? "Webhook URL is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.webhookUrl && <p className="text-red text-sm mt-1">{errors.webhookUrl.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        Enter the URL where coupon redemption webhooks should be sent
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='flex flex-col mt-0 gap-4'>
                            <div className="flex flex-col px-5 py-4 gap-3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                <SubHeading text={"Link A Challenge"} />
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                    {/* Challenge category */}
                                    <div className='flex flex-col gap-2 col-span-1'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Challenge Category</label>
                                        <NewDropdown
                                            name={"challengeCategory"}
                                            dataList={challengeCategoryList}
                                            placeholder={'Select Challenge Category'}
                                            value={watchedChallengeCategory}
                                            onChange={(e) => {
                                                setValue("challengeCategory", e.value);
                                                setSelectedCategoryId(e.value);
                                                setValue("connectedChallenge", ""); // reset challenge on category change
                                            }}
                                        />
                                    </div>
                                    {/* Challenge */}
                                    <div className='flex flex-col gap-2 w-full col-span-2'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                            <span className="text-red text-xs inline">* </span>
                                            Challenge</label>
                                        <NewDropdown
                                            name={"connectedChallenge"}
                                            dataList={filteredChallengeList}
                                            placeholder={'Select Challenge'}
                                            value={watchedConnectedChallenge}
                                            onChange={(e) => {
                                                setValue("connectedChallenge", e.value);
                                                if (e.value) {
                                                    trigger("connectedChallenge");
                                                }
                                            }}
                                        />
                                        {errors.connectedChallenge && <p className="text-red text-sm mt-1">{errors.connectedChallenge.message}</p>}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3 w-full text-sm font-semibold max-md:pl-5 max-md:max-w-full">
                                    {/* Sponsor Message */}
                                    <div className="flex flex-col gap-2">
                                        <label className="text-[14px] font-[600] text-gray-900">
                                            Sponsor Message (to be displayed on the app)
                                        </label>
                                        <div className="w-full bg-white rounded-md">
                                            <RichTextEditor
                                                placeholder="Enter your sponsor message that will be displayed to app users..."
                                                name="sponsorMessage"
                                                value={getValues("sponsorMessage")}
                                                onChange={(value) => setValue("sponsorMessage", value)}
                                                error={errors.sponsorMessage}
                                            />
                                        </div>
                                        {errors.sponsorMessage && <p className="text-red text-sm">{errors.sponsorMessage.message}</p>}
                                    </div>
                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                        {/* Type of discount */}
                                        {/* <div className="flex flex-col gap-2 basis-1/2">
                                        <label className="text-[14px] font-[600] text-gray-900">Type of Discount</label>
                                        <NewDropdown
                                            name={"typeOfDiscount"}
                                            dataList={[
                                                { value: 'Percentage Discount', label: 'Percentage Discount' },
                                                { value: 'Flat Amount', label: 'Flat Amount' },
                                            ]}
                                            placeholder={'Select Type of Discount'}
                                            value={watchedTypeOfDiscount}
                                            onChange={(e) => setValue("typeOfDiscount", e.value)}
                                        />
                                    </div> */}
                                        {/* Discount Value */}
                                        {/* <div className="flex flex-col gap-2 basis-1/2">
                                        <label className="text-[14px] font-[600] text-gray-900">Discount Value</label>
                                        <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                            <input
                                                type="text"
                                                placeholder="Select Discount Value"
                                                className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                {...register("discountValue", { required: false })}
                                            />
                                        </div>
                                    </div> */}
                                        {/* Coupon Code Format */}
                                        {/* <div className='flex flex-col gap-2 basis-1/2'>
                                        <label className='text-[14px] font-[600] text-gray-900'>Coupon Code Format</label>
                                        <NewDropdown
                                            name={"couponCodeFormat"}
                                            dataList={[
                                                { value: 'Unique', label: 'Unique' },
                                                { value: 'Universal', label: 'Universal' },
                                            ]}
                                            placeholder={'Select Coupon Code Format'}
                                            value={watchedCouponCodeFormat}
                                            onChange={(e) => setValue("couponCodeFormat", e.value)}
                                        />
                                        {errors.couponCodeFormat && <p className="text-red text-sm">{errors.couponCodeFormat.message}</p>}
                                    </div> */}
                                    </div>
                                    {/* <div className='flex flex-col gap-0'>
                        <label className='text-[14px] font-[600] text-gray-900'>Discount Mode</label>
                        <div className="flex gap-12 items-center mt-1 w-full bg-white border border-solid border-navBtn px-4 py-3 rounded-md text-slate-400">
                            {['Digital Coupon', 'Others'].map((platform) => (
                                <div key={platform} className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="discountMode"
                                        id={`promotion-frequency-${platform}`}
                                        value={platform}
                                        {...register("discountMode")}
                                        className="w-5 h-5 text-white border-gray-300  focus:ring-black accent-black"
                                    />
                                    <label
                                        htmlFor={`promotion-frequency-${platform}`}
                                        className="text-[12px] font-[600] text-gray-700 capitalize"
                                    >
                                        {platform}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div> */}
                                    {/* <div className='flex gap-3 items-center mt-3'>
                                    {
                                        watchedCouponCodeFormat === 'Unique' ? (
                                            <div className="flex flex-col gap-2 basis-1/2">
                                                Max coupon count
                                                <label className="text-[14px] font-[600] text-gray-900">Max Coupon Count</label>
                                                <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                    <input
                                                        type="text"
                                                        placeholder="Select Max Coupon Count"
                                                        className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                                        {...register("maxCouponCount", { required: false })}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-2 basis-1/2">
                                                Max Redemption count
                                                <label className="text-[14px] font-[600] text-gray-900">Max Redemption Count</label>
                                                <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                    <input
                                                        type="text"
                                                        placeholder="Select Max Redemption Count"
                                                        className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                                        {...register("maxRedemptionCount", { required: false })}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    }
                                </div> */}
                                    {/* Coupon Media */}
                                    {/* <div className='flex flex-col gap-2'>
                                    <MediaUpload
                                        className={`h-auto`}
                                        clearMedia={mediaClear}
                                        label={"Upload Coupon Media"}
                                        existingImage={couponMedia}
                                        setImageUpload={setCouponMedia}
                                        stateImage={couponMedia}
                                        setStateImageUpload={setCouponMedia}
                                    />
                                </div> */}
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
            {/* Link A Blog */}
            {
                watchedCampaignType === 'Sponsor a Blog' && (
                    <div className='flex flex-col mt-0 gap-4'>
                        <div className="flex flex-col px-5 py-4 gap-3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                            <SubHeading text={"Link A Blog"} />
                            {/* Link a blog */}
                            <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Link A Blog</label>
                                    <NewDropdown
                                        dataList={blogList}
                                        name={'blogId'}
                                        dropDownContentType={'blog'}
                                        placeholder={'Select Blog'}
                                        value={watchedBlogId}
                                        onChange={(e) => {
                                            setValue("blogId", e.value);
                                            if (e.value) {
                                                trigger("blogId");
                                            }
                                        }
                                        }
                                    />
                                    {errors.blogId && <p className="text-red text-sm mt-1">{errors.blogId.message}</p>}
                                </div>
                            </div>
                            {/* Sponsor Message */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[14px] font-[600] text-gray-900">
                                    Sponsor Message (to be displayed on the app)
                                </label>
                                <div className="w-full bg-white rounded-md">
                                    <RichTextEditor
                                        placeholder="Enter your sponsor message that will be displayed to app users..."
                                        name="sponsorMessage"
                                        value={getValues("sponsorMessage")}
                                        onChange={(value) => setValue("sponsorMessage", value)}
                                        error={errors.sponsorMessage}
                                    />
                                </div>
                                {errors.sponsorMessage && <p className="text-red text-sm">{errors.sponsorMessage.message}</p>}
                            </div>
                            {/* Link */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[14px] font-[600] text-gray-900">Link</label>
                                <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Select Link"
                                        className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                        {...register("link", { required: false })}
                                    />
                                </div>
                            </div>
                            {/* Sponsored Image */}
                            {/* <div className='flex flex-col gap-2'>
                                <MediaUpload
                                    className={`h-auto`}
                                    clearMedia={mediaClear}
                                    label={"Upload Sponsored Image"}
                                    existingImage={couponMedia}
                                    setImageUpload={setCouponMedia}
                                    stateImage={couponMedia}
                                    setStateImageUpload={setCouponMedia}
                                />
                            </div> */}
                        </div>
                    </div>
                )
            }
            {/* Promote Brand/Product */}
            {
                watchedCampaignType === 'Promote Brand/Product' && (
                    <>
                        {/* <div className='flex flex-col mt-0 gap-4'>
                            <div className="flex flex-col px-5 py-4 gap-3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                <SubHeading text={"Promote Brand/Product"} />
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                    Promotion Type
                                    <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                        <div className='flex flex-col gap-2 col-span-1'>
                                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Select Promotion Type</label>
                                            <NewDropdown
                                                dataList={[
                                                    { value: 'Product', label: 'Product level promotion' },
                                                    { value: 'Brand', label: 'Brand level promotion' },
                                                ]}
                                                name={'promotionType'}
                                                dropDownContentType={'promotionType'}
                                                placeholder={'Select Promotion Type'}
                                                value={watchedPromotionType}
                                                onChange={(e) => {
                                                    setValue("promotionType", e.value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    Link a Product
                                    <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                        <div className='flex flex-col gap-2 col-span-1'>
                                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Link A Product</label>
                                            <NewDropdown
                                                dataList={productsList || []}
                                                name={'productIdsArray'}
                                                allowMultiple={true}
                                                dropDownContentType={'product'}
                                                placeholder={'Select Products'}
                                                value={watchedProductIdsArray}
                                                onChange={(e) => {
                                                    setValue("productIdsArray", e.value);
                                                    if (e.value && e.value.length > 0) {
                                                        trigger("productIdsArray");
                                                    }
                                                }}
                                            />
                                            {errors.productIdsArray && <p className="text-red text-sm mt-1">{errors.productIdsArray.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                Promotional Link
                                <div className="flex flex-col gap-2 basis-1/2">
                                    <label className="text-[14px] font-[600] text-gray-900">Promotional Link</label>
                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Select Promotional Link"
                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                            {...register("promotionalLink", { required: false })}
                                        />
                                    </div>
                                </div>
                                Sponsor Message
                                <div className="flex flex-col gap-2">
                                    <label className="text-[14px] font-[600] text-gray-900">
                                        Sponsor Message (to be displayed on the app)
                                    </label>
                                    <div className="w-full bg-white rounded-md">
                                        <RichTextEditor
                                            placeholder="Enter your sponsor message that will be displayed to app users..."
                                            name="sponsorMessage"
                                            value={getValues("sponsorMessage")}
                                            onChange={(value) => setValue("sponsorMessage", value)}
                                            error={errors.sponsorMessage}
                                        />
                                    </div>
                                    {errors.sponsorMessage && <p className="text-red text-sm">{errors.sponsorMessage.message}</p>}
                                </div>
                            </div>
                        </div> */}

                        {/* Rewards Section for Promote Brand/Product */}
                        <div className='flex flex-col mt-0 gap-4'>
                            <div className="flex flex-col px-5 py-4 gap-3 basis-1/3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                <SubHeading text={"Rewards"} />
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                    {/* Challenge category */}
                                    <div className='flex flex-col gap-2 col-span-1'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                            {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                            Reward Type
                                        </label>
                                        <NewDropdown
                                            name={"Reward Type"}
                                            dataList={[
                                                { value: "DiscountCoupon", label: "Discount Coupon" },
                                            ]}
                                            placeholder={'Select Reward Type'}
                                            value={watchedRewardType}
                                            onChange={(e) => setValue("rewardType", e.value)}
                                        />
                                        {errors.rewardType && <p className="text-red text-sm mt-1">{errors.rewardType.message}</p>}
                                    </div>


                                    {
                                        watchedRewardType === 'DiscountCoupon' && (
                                            <>
                                                {/* Type of discount */}
                                                {/* <div className="flex flex-col gap-3 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Type of Discount
                                                    </label>
                                                    <NewDropdown
                                                        name={"typeOfDiscount"}
                                                        dataList={[
                                                            { value: 'Percentage Discount', label: 'Percentage Discount' },
                                                            { value: 'Flat Amount', label: 'Flat Amount' },
                                                        ]}
                                                        placeholder={'Select Type of Discount'}
                                                        value={watchedTypeOfDiscount}
                                                        onChange={(e) => setValue("typeOfDiscount", e.value)}
                                                    />
                                                    {errors.typeOfDiscount && <p className="text-red text-sm mt-1">{errors.typeOfDiscount.message}</p>}
                                                </div> */}
                                                {/* Discount Value */}
                                                {/* <div className="flex flex-col gap-2 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Discount Value
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Select Discount Value"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("discountValue", { 
                                                                required: watchedCampaignType === 'Promote Brand/Product' && active ? "Discount Value is required" : false 
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.discountValue && <p className="text-red text-sm mt-1">{errors.discountValue.message}</p>}
                                                </div> */}
                                                {/* Coupon Code Format */}
                                                <div className='flex flex-col gap-2 basis-1/2'>
                                                    <label className='text-[14px] font-[600] text-gray-900'>
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Coupon Code Format
                                                    </label>
                                                    <NewDropdown
                                                        name={"couponCodeFormat"}
                                                        dataList={[
                                                            { value: 'unique', label: 'Unique' },
                                                            { value: 'universal', label: 'Universal' },
                                                        ]}
                                                        placeholder={'Select Coupon Code Format'}
                                                        value={watchedCouponCodeFormat}
                                                        onChange={(e) => setValue("couponCodeFormat", e.value)}
                                                    />
                                                    {errors.couponCodeFormat && <p className="text-red text-sm mt-1">{errors.couponCodeFormat.message}</p>}
                                                </div>
                                                {/* Brand Dropdown - Optional */}
                                                <div className='flex flex-col gap-2 basis-1/2'>
                                                    <label className='text-[14px] font-[600] text-gray-900'>
                                                        {watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' && active && <span className="text-red text-xs inline">* </span>}
                                                        Brand {watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' ? '(Filter for Products)' : watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' ? '' : '(Optional)'}
                                                    </label>
                                                    <NewDropdown
                                                        name={"brand"}
                                                        dataList={brandDropDown || []}
                                                        placeholder={watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' ? 'Select Brand to Filter Products' : watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand' ? 'Select Brand' : 'Select Brand (Optional)'}
                                                        value={watchedBrand}
                                                        onChange={(e) => {
                                                            setValue("brand", e.value);
                                                            setValue("productServices", []); // Reset product services when brand changes
                                                        }}
                                                    />
                                                    {errors.brand && <p className="text-red text-sm mt-1">{errors.brand.message}</p>}
                                                </div>
                                                {/* Product/Services MultiSelect Dropdown */}
                                                {!(watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'brand') && (
                                                    <div className='flex flex-col gap-2 basis-1/2'>
                                                        <label className='text-[14px] font-[600] text-gray-900'>
                                                            {((watchedCampaignType === 'Sponsor a Challenge' && active) || (watchedCampaignType === 'Promote Brand/Product' && watchedPromotionType === 'product' && active)) && <span className="text-red text-xs inline">* </span>}
                                                            Product/Services
                                                        </label>
                                                        <NewDropdown
                                                            name={"productServices"}
                                                            dataList={getFilteredProducts()}
                                                            allowMultiple={true}
                                                            placeholder={'Select Product/Services'}
                                                            value={watchedProductServices}
                                                            onChange={(e) => setValue("productServices", Array.isArray(e.value) ? e.value : [])}
                                                        />
                                                        {errors.productServices && <p className="text-red text-sm mt-1">{errors.productServices.message}</p>}
                                                    </div>
                                                )}
                                                {/* Link to Use Coupon */}
                                                <div className="flex flex-col gap-2 basis-1/2">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && !watchedCouponRedemptionInstructions && <span className="text-red text-xs inline">* </span>}
                                                        Link to Use Coupon
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Link to Use Coupon"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("linkToUseCoupon", { required: false })}
                                                        />
                                                    </div>
                                                    {errors.linkToUseCoupon && <p className="text-red text-sm mt-1">{errors.linkToUseCoupon.message}</p>}
                                                </div>
                                                {/* Coupon Redemption Instructions */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && !getValues("linkToUseCoupon") && <span className="text-red text-xs inline">* </span>}
                                                        Coupon Redemption Instructions
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <textarea
                                                            placeholder="Enter detailed instructions on how users can redeem this coupon..."
                                                            rows={3}
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary resize-y min-h-[80px]"
                                                            {...register("couponRedemptionInstructions", { required: false })}
                                                        />
                                                    </div>
                                                    {errors.couponRedemptionInstructions && <p className="text-red text-sm mt-1">{errors.couponRedemptionInstructions.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active
                                                            ? "Either provide redemption instructions OR link to use coupon (at least one is required)"
                                                            : "Provide step-by-step instructions for users on how to redeem the coupon"
                                                        }
                                                    </p>
                                                </div>
                                            </>
                                        )
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Redemption Tracking Section for Promote Brand/Product */}
                        {watchedRewardType === 'DiscountCoupon' && (
                            <div className='flex flex-col mt-0 gap-4'>
                                <div className="flex flex-col px-5 py-4 gap-3 basis-1/3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                                    <SubHeading text={"Redemption Tracking"} />
                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                                        {/* Redemption Type */}
                                        <div className='flex flex-col gap-2 col-span-1'>
                                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                                {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                Redemption Type
                                            </label>
                                            <NewDropdown
                                                name={"redemptionType"}
                                                dataList={[
                                                    { value: "WebHook", label: "WebHook" },
                                                    { value: "PullApi", label: "Pull Api" },
                                                    { value: "NoTracking", label: "No Tracking" },
                                                ]}
                                                placeholder={'Select Redemption Type'}
                                                value={watchedRedemptionType}
                                                onChange={(e) => setValue("redemptionType", e.value)}
                                            />
                                            {errors.redemptionType && <p className="text-red text-sm mt-1">{errors.redemptionType.message}</p>}
                                        </div>

                                        {/* Pull API Fields - Only show if redemption type is Pull Api */}
                                        {watchedRedemptionType === 'PullApi' && (
                                            <>
                                                {/* Type of API */}
                                                <div className='flex flex-col gap-2 col-span-1'>
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Type of API
                                                    </label>
                                                    <NewDropdown
                                                        name={"apiType"}
                                                        dataList={[
                                                            { value: "GET", label: "GET" },
                                                            { value: "POST", label: "POST" },
                                                        ]}
                                                        placeholder={'Select API Type'}
                                                        value={watchedApiType}
                                                        onChange={(e) => setValue("apiType", e.value)}
                                                    />
                                                    {errors.apiType && <p className="text-red text-sm mt-1">{errors.apiType.message}</p>}
                                                </div>

                                                {/* API Endpoint */}
                                                <div className="flex flex-col gap-2 col-span-1">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        API Endpoint
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter API Endpoint"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("apiEndpoint", {
                                                                required: watchedCampaignType === 'Promote Brand/Product' && active && watchedRedemptionType === 'PullApi' ? "API Endpoint is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.apiEndpoint && <p className="text-red text-sm mt-1">{errors.apiEndpoint.message}</p>}
                                                </div>

                                                {/* Headers */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Headers (JSON format)
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <textarea
                                                            placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary min-h-[80px]"
                                                            {...register("apiHeaders", {
                                                                required: watchedCampaignType === 'Promote Brand/Product' && active && watchedRedemptionType === 'PullApi' ? "API Headers is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.apiHeaders && <p className="text-red text-sm mt-1">{errors.apiHeaders.message}</p>}
                                                </div>

                                                {/* Body JSON - Only show if API type is POST */}
                                                {watchedApiType === 'POST' && (
                                                    <div className="flex flex-col gap-2 col-span-3">
                                                        <label className="text-[14px] font-[600] text-gray-900">
                                                            {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                            Body JSON (with replaceable variables)
                                                        </label>
                                                        <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                            <textarea
                                                                placeholder='{"couponCode": "{{couponCode}}", "userId": "{{userId}}", "amount": "{{amount}}"}'
                                                                className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary min-h-[120px]"
                                                                {...register("apiBody", {
                                                                    required: watchedCampaignType === 'Promote Brand/Product' && active && watchedRedemptionType === 'PullApi' && watchedApiType === 'POST' ? "API Body is required" : false
                                                                })}
                                                            />
                                                        </div>
                                                        {errors.apiBody && <p className="text-red text-sm mt-1">{errors.apiBody.message}</p>}
                                                        <p className="text-xs text-gray-500">
                                                            Use double curly braces for replaceable variables: {`{{variableName}}`}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Daily Run Time - Always show for Pull API */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Daily Run Time (Select hours when the API should run)
                                                    </label>
                                                    <NewDropdown
                                                        name={"dailyRunTime"}
                                                        dataList={getTimeSlots()}
                                                        allowMultiple={true}
                                                        placeholder={'Select Daily Run Time Hours'}
                                                        value={watchedDailyRunTime}
                                                        onChange={(e) => setValue("dailyRunTime", Array.isArray(e.value) ? e.value : [])}
                                                    />
                                                    {errors.dailyRunTime && <p className="text-red text-sm mt-1">{errors.dailyRunTime.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        Select one or more hours when the API should be called daily
                                                    </p>
                                                </div>
                                            </>
                                        )}

                                        {/* WebHook Fields - Only show if redemption type is WebHook */}
                                        {watchedRedemptionType === 'WebHook' && (
                                            <>
                                                {/* Webhook URL */}
                                                <div className="flex flex-col gap-2 col-span-3">
                                                    <label className="text-[14px] font-[600] text-gray-900">
                                                        {watchedCampaignType === 'Promote Brand/Product' && active && <span className="text-red text-xs inline">* </span>}
                                                        Webhook URL
                                                    </label>
                                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                        <input
                                                            type="url"
                                                            placeholder="https://example.com/webhook/coupon-redeemed"
                                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                                            {...register("webhookUrl", {
                                                                required: watchedCampaignType === 'Promote Brand/Product' && active && watchedRedemptionType === 'WebHook' ? "Webhook URL is required" : false
                                                            })}
                                                        />
                                                    </div>
                                                    {errors.webhookUrl && <p className="text-red text-sm mt-1">{errors.webhookUrl.message}</p>}
                                                    <p className="text-xs text-gray-500">
                                                        Enter the URL where coupon redemption webhooks should be sent
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )
            }
            {/* Promote an Event */}
            {
                watchedCampaignType === 'Promote an Event' && (
                    <div className='flex flex-col mt-0 gap-4'>
                        <div className="flex flex-col px-5 py-4 gap-3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                            <SubHeading text={"Promote an Event"} />
                            {/* Promote an Event */}
                            <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Select Event</label>
                                    <NewDropdown
                                        dataList={eventList}
                                        name={'eventId'}
                                        dropDownContentType={'event'}
                                        placeholder={'Select Event'}
                                        value={watchedEventId}
                                        onChange={(e) => {
                                            setValue("eventId", e.value);
                                            if (e.value) {
                                                trigger("eventId");
                                            }
                                        }
                                        }
                                    />
                                    {errors.eventId && <p className="text-red text-sm mt-1">{errors.eventId.message}</p>}
                                </div>
                            </div>
                            {/* certificate available from NGO? */}
                            <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">certificate available from NGO?</label>
                                    <NewDropdown
                                        dataList={[
                                            { value: 'Yes', label: 'Yes' },
                                            { value: 'No', label: 'No' },
                                        ]}
                                        name={'certificateAvailable'}
                                        placeholder={'Certificate Available?'}
                                        value={watchedCertificateAvailable}
                                        onChange={(e) => {
                                            setValue("certificateAvailable", e.value);
                                        }
                                        }
                                    />
                                </div>
                            </div>
                            {/* Promote an Event */}
                            <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                <div className='flex flex-col gap-2 col-span-1'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Certificate Template(Link)</label>
                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Select Location"
                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                            {...register("certificateTemplate", { required: false })}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Sponsor Message */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[14px] font-[600] text-gray-900">
                                    Sponsor Message (to be displayed on the app)
                                </label>
                                <div className="w-full bg-white rounded-md">
                                    <RichTextEditor
                                        placeholder="Enter your sponsor message that will be displayed to app users..."
                                        name="sponsorMessage"
                                        value={getValues("sponsorMessage")}
                                        onChange={(value) => setValue("sponsorMessage", value)}
                                        error={errors.sponsorMessage}
                                    />
                                </div>
                                {errors.sponsorMessage && <p className="text-red text-sm">{errors.sponsorMessage.message}</p>}
                            </div>
                            {/* Upload certificate template */}
                            {/* <div className='flex flex-col gap-2'>
                                <MediaUpload
                                    className={`h-auto`}
                                    clearMedia={mediaClear}
                                    label={"Upload certificate template"}
                                    existingImage={couponMedia}
                                    setImageUpload={setCouponMedia}
                                    stateImage={couponMedia}
                                    setStateImageUpload={setCouponMedia}
                                />
                            </div> */}
                            {/* Upload actual certificate */}
                            {/* <div className='flex flex-col gap-2'>
                                <MediaUpload
                                    className={`h-auto`}
                                    clearMedia={mediaClear}
                                    label={"Upload actual certificate"}
                                    existingImage={couponMedia}
                                    setImageUpload={setCouponMedia}
                                    stateImage={couponMedia}
                                    setStateImageUpload={setCouponMedia}
                                />
                            </div> */}
                        </div>
                    </div>
                )
            }
            {/* Advertisement */}
            {
                watchedCampaignType === 'Advertisement' && (
                    <div className='flex flex-col mt-0 gap-4'>
                        <div className="flex flex-col px-5 py-4 gap-3 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full">
                            <SubHeading text={"Advertisement"} />
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Select Advertisement */}
                                <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                    <div className='flex flex-col gap-2 col-span-1'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                            {watchedCampaignType === 'Advertisement' && active && <span className="text-red text-xs inline">* </span>}
                                            Select Advertisement
                                        </label>
                                        <NewDropdown
                                            dataList={adsList || []}
                                            name={'advertisement'}
                                            placeholder={'Select Advertisement'}
                                            value={watchedAdvertisement}
                                            onChange={(e) => {
                                                setValue("advertisement", e.value);
                                                if (e.value) {
                                                    trigger("advertisement");
                                                }
                                            }}
                                        />
                                        {errors.advertisement && <p className="text-red text-sm mt-1">{errors.advertisement.message}</p>}
                                    </div>
                                </div>
                                {/* Advertisement Slot */}
                                <div className='grid grid-cols-1 md:grid-cols-1 gap-4'>
                                    <div className='flex flex-col gap-2 col-span-1'>
                                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                            {watchedCampaignType === 'Advertisement' && active && <span className="text-red text-xs inline">* </span>}
                                            Select Advertisement Slot
                                        </label>
                                        <NewDropdown
                                            dataList={getFreeSlots()}
                                            name={'advertisementSlot'}
                                            placeholder={'Select Advertisement Slot'}
                                            value={watchedAdvertisementSlot}
                                            onChange={(e) => {
                                                setValue("advertisementSlot", e.value);
                                                if (e.value) {
                                                    trigger("advertisementSlot");
                                                }
                                            }}
                                        />
                                        {errors.advertisementSlot && <p className="text-red text-sm mt-1">{errors.advertisementSlot.message}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Plant Trees Campaign Section */}
            {
                watchedCampaignType === 'Plant Trees' && (
                    <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                        <div className='flex flex-col gap-3'>
                            <SubHeading text={"Plant Trees Campaign Details"} />

                            {/* Project Selection and KPIs */}
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Plantation Project */}
                                <div className='flex flex-col gap-2'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                        {watchedCampaignType === 'Plant Trees' && active && <span className="text-red text-xs inline">* </span>}
                                        Select Plantation Project
                                    </label>
                                    <NewDropdown
                                        // dataList={getPlantationProjects()}
                                        dataList={projectsList}
                                        name={'plantationProject'}
                                        placeholder={'Select Plantation Project'}
                                        value={watchedPlantationProject}
                                        onChange={(e) => {
                                            setValue("plantationProject", e.value);
                                            handlePlantationProjectChange(e.value);
                                            if (e.value) {
                                                trigger("plantationProject");
                                            }
                                        }}
                                    />
                                    {errors.plantationProject && <p className="text-red text-sm mt-1">{errors.plantationProject.message}</p>}
                                </div>

                                {/* Link Event */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>Link an Event</label>
                                    <NewDropdown
                                        dataList={eventList}
                                        name={"event"}
                                        placeholder={'Link an Event'}
                                        value={watchedEventId}
                                        onChange={(e) => {
                                            setValue("eventId", e.value);
                                        }}
                                    />
                                    {errors.eventId && <p className="text-red text-sm">{errors.eventId.message}</p>}
                                </div>
                            </div>

                            {/* Points and Certificate Settings */}
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Spend Points per Tree */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>
                                        {watchedCampaignType === 'Plant Trees' && active && <span className="text-red text-xs inline">* </span>}
                                        Spend Points per Tree
                                    </label>
                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="number"
                                            {...register("spendPointsPerTree", {
                                                required: watchedCampaignType === 'Plant Trees' && active ? "Spend Points per Tree is required" : false,
                                                min: { value: 1, message: "Must be at least 1" }
                                            })}
                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                            placeholder="Enter points required per tree"
                                        />
                                    </div>
                                    {errors.spendPointsPerTree && <p className="text-red text-sm">{errors.spendPointsPerTree.message}</p>}
                                </div>

                                {/* Max Certificate Count */}
                                <div className='flex flex-col gap-2'>
                                    <label className='text-[14px] font-[600] text-gray-900'>
                                        {watchedCampaignType === 'Plant Trees' && active && <span className="text-red text-xs inline">* </span>}
                                        Max Certificate Count
                                    </label>
                                    <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="number"
                                            {...register("maxCertificateCount", {
                                                required: watchedCampaignType === 'Plant Trees' && active ? "Max Certificate Count is required" : false,
                                                min: { value: 1, message: "Must be at least 1" }
                                            })}
                                            className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary"
                                            placeholder="Enter maximum certificates to issue"
                                        />
                                    </div>
                                    {errors.maxCertificateCount && <p className="text-red text-sm">{errors.maxCertificateCount.message}</p>}
                                </div>
                            </div>

                            {/* Advertisement Section */}
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Advertisement */}
                                <div className='flex flex-col gap-2'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                        {watchedCampaignType === 'Plant Trees' && active && <span className="text-red text-xs inline">* </span>}
                                        Select Advertisement
                                    </label>
                                    <NewDropdown
                                        dataList={adsList || []}
                                        name={'advertisement'}
                                        placeholder={'Select Advertisement'}
                                        value={watchedAdvertisement}
                                        onChange={(e) => {
                                            setValue("advertisement", e.value);
                                            if (e.value) {
                                                trigger("advertisement");
                                            }
                                        }}
                                    />
                                    {errors.advertisement && <p className="text-red text-sm mt-1">{errors.advertisement.message}</p>}
                                </div>

                                {/* Advertisement Slot */}
                                <div className='flex flex-col gap-2'>
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                                        {watchedCampaignType === 'Plant Trees' && active && <span className="text-red text-xs inline">* </span>}
                                        Select Advertisement Slot
                                    </label>
                                    <NewDropdown
                                        dataList={getFreeSlots()}
                                        name={'advertisementSlot'}
                                        placeholder={'Select Advertisement Slot'}
                                        value={watchedAdvertisementSlot}
                                        onChange={(e) => {
                                            setValue("advertisementSlot", e.value);
                                            if (e.value) {
                                                trigger("advertisementSlot");
                                            }
                                        }}
                                    />
                                    {errors.advertisementSlot && <p className="text-red text-sm mt-1">{errors.advertisementSlot.message}</p>}
                                </div>
                            </div>

                            {/* Project Information Display (Read-only) */}
                            {watchedPlantationProject && (
                                <div className='flex flex-col gap-3'>
                                    <h4 className='text-[16px] font-[600] text-gray-900'>Project Information</h4>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                        {/* Project Name */}
                                        <div className='flex flex-col gap-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Project Name</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <input
                                                    type="text"
                                                    value={getValues("projectName")}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* Project Location */}
                                        <div className='flex flex-col gap-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Project Location</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <input
                                                    type="text"
                                                    value={getValues("projectLocation")}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* Project Address */}
                                        <div className='flex flex-col gap-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Project Address</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <input
                                                    type="text"
                                                    value={getValues("projectAddress")}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* Project Date */}
                                        <div className='flex flex-col gap-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Project Date</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <input
                                                    type="text"
                                                    value={getValues("projectDate") ? new Date(getValues("projectDate")).toLocaleDateString() : ''}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* Project Short Description */}
                                        <div className='flex flex-col gap-2 col-span-1 md:col-span-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Short Description</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <textarea
                                                    value={getValues("projectShortDescription")}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    rows={3}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {/* Project Long Description */}
                                        <div className='flex flex-col gap-2 col-span-1 md:col-span-2'>
                                            <label className='text-[14px] font-[600] text-gray-600'>Long Description</label>
                                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                                <textarea
                                                    value={getValues("projectLongDescription")}
                                                    className="px-3 py-2 text-sm text-gray-900 bg-gray-100 rounded-md w-full"
                                                    rows={4}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Notes/Comments Section - Common for all campaign types */}
            <div className="flex flex-col px-5 py-4 w-full text-sm font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:pl-5 max-md:max-w-full gap-6">
                <div className='flex flex-col gap-3'>
                    <SubHeading text={"Notes/Comments"} />
                    <div className='grid grid-cols-1 gap-4'>
                        <div className='flex flex-col gap-2'>
                            <label className='text-[14px] font-[600] text-gray-900'>
                                Notes/Comments (Optional)
                            </label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <textarea
                                    placeholder="Enter any additional notes or comments about this campaign..."
                                    rows={4}
                                    className="flex-1 px-4 py-3 pb-2.5 rounded-md text-base outline-primary resize-y min-h-[100px]"
                                    {...register("notes", { required: false })}
                                />
                            </div>
                            <p className="text-xs text-gray-500">
                                Add any additional information, special instructions, or comments about this campaign
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 flex gap-5 items-center justify-between">
                <ActionButton
                    variant="reset"
                    text="Reset"
                    handleSubmit={() => {
                        reset();
                        setCoverImage(null);
                        setMediaClear(true);
                        setShowInHomePage(false);
                    }}
                />
                <div className="flex items-center gap-3">
                    <ActionButton
                        variant="cancel"
                        text="Cancel"
                        handleSubmit={() => navigate('/campaign-management/list-campaign')}
                    />
                    <ActionButton
                        variant="submit"
                        type={'submit'}
                        isSubmit={true}
                        text="Save"
                        handleSubmit={() => {
                            console.log('Save button clicked!'); // Debug log
                            console.log('Current form errors:', errors); // Debug log
                        }}
                        disabled={
                            isCreating
                            || isEditing
                        }
                    />
                </div>
            </div>
            <ToastContainer
                position="top-right"
            />
        </form >
    );
}

export default CreateNewCampaignCopy